// Translations for the Prague Tour Guide Website
// German (DE) and English (EN)
import { blogTranslations } from './blogTranslations';

export const translations = {
  'nav.home': {
    en: 'Home',
    de: 'Startseite',
  },
  'nav.tours': {
    en: 'Tours',
    de: 'Touren',
  },
  'nav.blog': {
    en: 'Blog',
    de: 'Blog',
  },
  'nav.contact': {
    en: 'Contact',
    de: 'Kontakt',
  },

  // New Post 13: Prettiest Winter Views in Prague
  'blog.post13.title': {
    en: 'Which Places Are the Prettiest to Visit in Prague in Winter? Top 12 Views',
    de: 'Welche Orte sind im Winter in Prag am schönsten? Top 12 Aussichtspunkte',
  },
  'blog.post13.excerpt': {
    en: 'Prague in winter glows with warm light, quiet alleys, and magical views. Here are the 12 prettiest Old Town vantage points—walkable, photogenic, and cozy.',
    de: 'Prag leuchtet im Winter in warmem Licht: stille Gassen, magische Ausblicke. Hier sind 12 der schönsten Altstadt-Perspektiven—fußläufig, fotogen und behaglich.',
  },
  'blog.post13.date': {
    en: 'October 25, 2025',
    de: '25. Oktober 2025',
  },
  'blog.post13.content': {
    en: `<h2>Wintry Prague Through a Local’s Eyes—A Magical Walk Among the City’s Prettiest Views</h2>
<p class="lead">Imagine the winter sun rising over Charles Bridge, the city cloaked in a gentle mist. As I step out on frosty cobblestones, lanterns glow softly and each breath forms clouds in the air. Join me on a cozy, step-by-step stroll through Prague’s loveliest winter corners—places where historic beauty meets seasonal enchantment.</p>
<hr />
<ol>
  <li>
    <h3>1. Charles Bridge (From Old Town Tower to Castle)</h3>
    <p>The city’s classic view: standing where the bridge begins, looking toward Prague Castle. In winter’s low sun or blue dusk, statues seem to come alive. I always recommend arriving early—locals cross in silence, and the chill is worth it for the scene.</p>
  </li>
  <li>
    <h3>2. Old Town Square (Astronomical Clock Corners)</h3>
    <p>Pastel buildings shimmer in the winter light. At the northwest corner, you’ll see the Astronomical Clock with spires and gentle crowds—a tableau made for photography.</p>
  </li>
  <li>
    <h3>3. Kaprova Street to Rudolfinum</h3>
    <p>Follow lamplight and tram tracks past elegant facades. I love capturing the castle on wintry diagonals, where Old Town reveals its grandeur.</p>
  </li>
  <li>
    <h3>4. Střelecký Ostrov (Shooter’s Island)</h3>
    <p>On this quiet island, the calm Vltava mirrors bridges and theatres. Early or late, water reflections are perfect for dreamy winter shots.</p>
  </li>
  <li>
    <h3>5. Národní Divadlo (National Theatre) Riverside</h3>
    <p>The riverbank here glows gold at dusk. Steam rises from trams and cafés on Národní Street—a truly cinematic Prague winter moment.</p>
  </li>
  <li>
    <h3>6. Letná “Stalin” Lookout (Letenská pláň)</h3>
    <p>A little beyond Old Town: the panorama of bridges and city layers is unbeatable. At dusk, light trails mix with winter haze.</p>
  </li>

  <div class="blog-inline-image"><img src="/images/blog-winter-cathedral.png" alt="St. Vitus Cathedral — III. Courtyard (winter)" loading="lazy" /><div class="image-credit">St. Vitus Cathedral — III. Courtyard</div></div>

  <li>
    <h3>7. Rudolfinum Embankment</h3>
    <p>Blue hour brings magical castle reflections framed by bridge arches and balustrades. Try long exposures in the cold air.</p>
  </li>
  <li>
    <h3>8. Kampa Sides of Charles Bridge</h3>
    <p>Step under the arches for willow-framed winter scenes. It’s quiet, perfect for romantic snapshots and old-time street views.</p>
  </li>
  <li>
    <h3>9. Old Town Bridge Tower (Upper Terrace)</h3>
    <p>If open, climb the tower for a postcard’s symmetry—bridge, river, castle. Remember extra layers; winter wind bites up high.</p>
  </li>
  <li>
    <h3>10. Pařížská Street to the Týn Church</h3>
    <p>Here, upscale shops meet neo-Gothic spires. At night, drizzle and puddles make for stunning reflections.</p>
  </li>
  <li>
    <h3>11. Clárov (By Mánes Bridge)</h3>
    <p>A secret riverbank for uncrowded castle views, especially magical at sunrise or in gentle snowfall.</p>
  </li>
  <li>
    <h3>12. Josefov Passages (near Kaprova)</h3>
    <p>Explore quiet passages and courtyards, glowing with warm windows and winter decor—pure Prague magic.</p>
  </li>
</ol>
<h3>Practical Winter Tips</h3>
<ul>
  <li>Aim for sunrise and blue hour; midday light is colder and less dramatic.</li>
  <li>Wear gloves for your camera and non-slip shoes.</li>
  <li>Take breaks at Café Louvre or Café Slavia.</li>
  <li>Respect icy spots and always step aside for locals.</li>
</ul>
<div class="blog-cta-box">
  <h3>Ready for a gorgeous winter walk?</h3>
  <p>Let me show you my favorite Prague angles—with stories, photos, and warm café stops. <a href="/book?tour=Winter%20Old%20Town%20Views#contact-title">Book your private winter tour</a>.</p>
</div>`,
    de: `<h2>Prag im Winter aus Sicht einer Einheimischen—Ein Magischer Spaziergang durch die schönsten Ecken</h2>
<p class="lead">Stellen Sie sich vor: Die Wintersonne erhebt sich über der Karlsbrücke, die Stadt liegt im Nebel. Auf frostigen Pflastersteinen leuchten Laternen und jeder Atemzug wird zu Wolken. Begleiten Sie mich auf einen gemütlichen Spaziergang zu Prags schönsten Winterplätzen—wo historische Pracht auf winterliche Zauber trifft.</p>
<hr />
<ol>
  <li>
    <h3>1. Karlsbrücke (Vom Altstädter Brückenturm zur Burg)</h3>
    <p>Der klassische Blick: Am Beginn der Brücke Richtung Prager Burg stehen. Im Sonnenuntergang oder der blauen Stunde wirken Statuen lebendig. Früh kommen lohnt sich—die Kälte ist es wert.</p>
  </li>
  <li>
    <h3>2. Altstädter Ring (Ecken der astronomischen Uhr)</h3>
    <p>Pastellfarbene Gebäude leuchten im Winterlicht. An der Nordwestecke sieht man die astronomische Uhr und die sanften Menschenmengen—perfekt für Fotos.</p>
  </li>
  <li>
    <h3>3. Kaprovastraße zum Rudolfinum</h3>
    <p>Laternen und Straßenbahnschienen führen an eleganten Fassaden vorbei. Ich liebe es, das Schloss auf winterlichen Diagonalen einzufangen—so zeigt die Altstadt ihre Größe.</p>
  </li>
  <li>
    <h3>4. Schützeninsel (Střelecký Ostrov)</h3>
    <p>Hier, auf der ruhigen Insel, spiegelt der Vltava Brücken und Theater. Reflektionen sind am Morgen oder Abend besonders schön.</p>
  </li>
  <li>
    <h3>5. Nationaltheater Ufer (Národní Divadlo)</h3>
    <p>Am Flussufer leuchtet alles golden bei Dämmerung. Dampf steigt von Straßenbahnen und Cafés auf Národní—ein wirklich filmreifer Wintermoment.</p>
  </li>
  <li>
    <h3>6. Letná „Stalin“-Aussichtspunkt (Letenská pláň)</h3>
    <p>Ein wenig außerhalb der Altstadt, aber: Das Panorama aus Brücken und Stadtlagen ist unvergleichlich schön—bei Dämmerung vermischen sich Lichtspuren mit dem Winternebel.</p>
  </li>

  <div class="blog-inline-image"><img src="/images/blog-winter-cathedral.png" alt="Veitsdom — III. Burghof (Winter)" loading="lazy" /><div class="image-credit">Veitsdom — III. Burghof</div></div>

  <li>
    <h3>7. Rudolfinum-Kai</h3>
    <p>Zur blauen Stunde erscheinen magische Schloss-Reflexionen, eingerahmt von Brückenbögen und Balustraden. Probieren Sie Langzeitbelichtungen in der Kälte!</p>
  </li>
  <li>
    <h3>8. Kampa-Seiten der Karlsbrücke</h3>
    <p>Unter den Bögen findet man weidenumrahmte Winteransichten. Ruhig und romantisch, ideal für Fotos und alte Straßenblicke.</p>
  </li>
  <li>
    <h3>9. Altstädter Brückenturm (obere Terrasse)</h3>
    <p>Bei geöffnetem Aufstieg liefert die Terrasse eine Postkarten-Symmetrie—Brücke, Fluss, Burg. Warme Kleidung nicht vergessen—der Winterwind ist stark hier oben.</p>
  </li>
  <li>
    <h3>10. Pařížská Straße zur Teynkirche</h3>
    <p>Exklusive Läden treffen auf neugotische Türme. Im Regen und bei Nacht entstehen wunderschöne Spiegelungen in Pfützen.</p>
  </li>
  <li>
    <h3>11. Clárov (bei der Mánes-Brücke)</h3>
    <p>Ein geheimer Flussufer für Burgblicke ohne Menschenmassen—zauberhaft bei Sonnenaufgang oder leichtem Schnee.</p>
  </li>
  <li>
    <h3>12. Josefov-Passagen (nahe Kaprova)</h3>
    <p>Entdecken Sie ruhige Passagen und Innenhöfe, die mit warmem Fensterlicht und Winterdekoration leuchten—reiner Prager Zauber.</p>
  </li>
</ol>
<h3>Praktische Wintertipps</h3>
<ul>
  <li>Morgens und zur blauen Stunde fotografieren; mittags ist das Licht kälter und weniger dramatisch.</li>
  <li>Handschuhe für die Kamera und rutschfeste Schuhe tragen.</li>
  <li>Pausen im Café Louvre oder im Café Slavia einlegen.</li>
  <li>Auf glatte Stellen achten und Einheimischen Vorrang lassen.</li>
</ul>
<div class="blog-cta-box">
  <h3>Bereit für einen wunderschönen Winterspaziergang?</h3>
  <p>Lassen Sie sich von mir die schönsten Prager Winterwinkel zeigen – mit Geschichten, Fotos und gemütlichen Cafépausen. <a href="/book?tour=Winter%20Altstadt%20Ausblicke#contact-title">Private Wintertour anfragen</a>.</p>
</div>`
  },

  // Hero Section
  'hero.title': {
    en: 'Discover Prague with Zuzana',
    de: 'Entdecken Sie Prag mit Zuzana',
  },
  'hero.subtitle': {
    en: 'Experience the magic of Prague through the eyes of a certified local expert',
    de: 'Erleben Sie die Magie Prags mit den Augen einer zertifizierten lokalen Expertin',
  },
  'hero.exploreTours': {
    en: 'Explore Tours',
    de: 'Touren erkunden',
  },
  'hero.contactMe': {
    en: 'Contact Me',
    de: 'Kontaktieren Sie mich',
  },
  'hero.sendEnquiry': {
    en: 'Send enquiry',
    de: 'Anfrage senden',
  },
  'hero.responsePromise': {
    en: 'I usually reply within 24 hours.',
    de: 'Ich antworte in der Regel innerhalb von 24 Stunden.',
  },

  // Features
  'features.historic.title': {
    en: 'Historic Sites',
    de: 'Historische Stätten',
  },
  'features.historic.desc': {
    en: 'Explore Prague Castle, Charles Bridge, and Old Town Square',
    de: 'Entdecken Sie die Prager Burg, Karlsbrücke und den Altstädter Ring',
  },
  'features.groups.title': {
    en: 'Flexible Groups',
    de: 'Flexible Gruppen',
  },
  'features.groups.desc': {
    en: 'From intimate tours to large groups - all welcome',
    de: 'Von intimen Führungen bis zu großen Gruppen – alle willkommen',
  },
  'features.expert.title': {
    en: 'Certified Expert',
    de: 'Zertifizierte Expertin & Spezialistin',
  },
  'features.expert.desc': {
    en: '40 years of experience with deep knowledge and engaging stories',
    de: 'Deutschsprachige Prag-Expertin mit 40 Jahren Erfahrung, tiefem Wissen und fesselnden Geschichten',
  },
  'features.gems.title': {
    en: 'Hidden Gems',
    de: 'Versteckte Schätze',
  },
  'features.gems.desc': {
    en: 'Discover secret spots tourists never find',
    de: 'Entdecken Sie geheime Orte, die Touristen nie finden',
  },

  // About Section
  'about.title': {
    en: 'Your Prague Guide',
    de: 'Ihre Prager Führerin',
  },
  'about.intro': {
    en: "I'm Ing. Zuzana Manová, a certified Prague expert guide. I've been guiding people through Prague since 1986. Forty years of watching this city change, keep its secrets, and surprise me. I still learn something new most weeks.",
    de: 'Ich bin Ing. Zuzana Manová – deutschsprachige Prag-Expertin und zertifizierte Stadtführerin. Seit 1986 führe ich Besucher durch Prag. Vierzig Jahre, in denen ich zugesehen habe, wie sich diese Stadt verändert, ihre Geheimnisse bewahrt und mich überrascht. Die meisten Wochen lerne ich noch etwas Neues.',
  },
  'about.expertise': {
    en: "I hold the standard Czech guide certification and a specialist accreditation from the Jewish Museum in Prague for the Jewish Quarter. Over the years I've walked these streets with school groups, families, corporate boards, film crews, and the occasional diplomat. The tour I give a retired couple from Munich is not the same tour I give a group of architecture students from London, and that's the point.",
    de: 'Als Spezialistin für Prager Geschichte und Kultur besitze ich die offizielle tschechische Stadtführer-Zertifizierung und eine zusätzliche Akkreditierung des Jüdischen Museums in Prag als Expertin für das Jüdische Viertel. Im Laufe der Jahre bin ich mit Schulklassen, Familien, Vorständen, Filmteams und dem einen oder anderen Diplomaten durch diese Straßen gegangen. Die deutschsprachige Führung, die ich einem Rentnerpaar aus München gebe, ist nicht dieselbe wie die für eine Gruppe Architekturstudenten aus London. Genau darum geht es.',
  },
  'about.promise': {
    en: "Tell me what you're curious about and I'll build the walk around that. If you don't know yet, even better. I have forty years of opinions about where to take you.",
    de: 'Sagen Sie mir, was Sie interessiert, und ich baue den Rundgang darum. Wenn Sie es noch nicht wissen, umso besser. Ich habe vierzig Jahre Meinungen darüber, wohin ich Sie mitnehmen sollte.',
  },
  'about.cta': {
    en: 'Get in Touch',
    de: 'Kontakt aufnehmen',
  },
  'about.photoTitle': {
    en: 'Meet Your Expert Guide',
    de: 'Treffen Sie Ihre Expertin',
  },
  'about.photoSubtitle': {
    en: 'Ing. Zuzana Manová - 40 Years of Excellence',
    de: 'Ing. Zuzana Manová - 40 Jahre Ehrfahrung',
  },
  'about.badge.line1': {
    en: 'Certified Expert,',
    de: 'Zertifizierte Expertin,',
  },
  'about.badge.line2': {
    en: '40+ Years Experience',
    de: '40+ Jahre Erfahrung',
  },
  'about.credential1': {
    en: 'Certified Tour Guide with Special Jewish Quarter Certification',
    de: 'Zertifizierte Reiseführerin mit Sonderzertifizierung für das Jüdische Viertel',
  },
  'about.credential2': {
    en: 'Former Fastest Woman in Czech Republic (1996)',
    de: 'Ehemalige schnellste Frau der Tschechischen Republik (1996)',
  },
  'about.credential3': {
    en: 'Thousands of Satisfied Clients from Around the World',
    de: 'Tausende zufriedene Kunden aus aller Welt',
  },
  'about.credential4': {
    en: 'Engaging Storyteller with Personal Approach',
    de: 'Fesselnde Geschichtenerzählerin mit persönlichem Ansatz',
  },

  // CTA Section
  'cta.title': {
    en: 'Ready to Explore Prague?',
    de: 'Bereit, Prag zu erkunden?',
  },
  'cta.subtitle': {
    en: 'Book your personalized tour today!',
    de: 'Buchen Sie noch heute Ihre persönliche Tour!',
  },
  'cta.call': {
    en: 'Call Now',
    de: 'Jetzt anrufen',
  },
  'cta.whatsapp': {
    en: 'WhatsApp',
    de: 'WhatsApp',
  },

  'home.badge': {
    en: 'Personal · Certified Expert · Local',
    de: 'Persönlich · Zertifizierte Expertin · Deutschsprachig',
  },
  'home.hero.line1': {
    en: 'Discover Prague',
    de: 'Entdecken Sie Prag',
  },
  'home.hero.line2': {
    en: 'with Zuzana Manová',
    de: 'mit Zuzana Manová',
  },
  'home.meta1': {
    en: '40+ years of experience',
    de: 'Über 40 Jahre Erfahrung',
  },
  'home.meta2': {
    en: 'Small groups & private tours',
    de: 'Kleine Gruppen & Privattouren',
  },
  'home.meta3': {
    en: '4.9 · 14 reviews on TripAdvisor',
    de: '4,9 · 14 Bewertungen auf TripAdvisor',
  },
  'home.hero.noPressure': {
    en: 'No commitment · Personal quote',
    de: 'Unverbindlich anfragen · Persönliches Angebot',
  },
  'home.hero.imageAlt': {
    en: 'View of Charles Bridge in Prague at dusk',
    de: 'Blick auf die Karlsbrücke in Prag bei Abenddämmerung',
  },
  'home.tours.teaser.title': {
    en: 'Popular tours',
    de: 'Beliebte Touren',
  },
  'home.tours.teaser.viewAll': {
    en: 'View all tours',
    de: 'Alle Touren ansehen',
  },
  'home.reviews.groupTitle': {
    en: 'Guest reviews',
    de: 'Bewertungen von Gästen',
  },
  'home.tripadvisor.sectionTitle': {
    en: 'Recent reviews on TripAdvisor',
    de: 'Aktuelle Bewertungen auf TripAdvisor',
  },
  'home.tripadvisor.viewAll': {
    en: 'All reviews on TripAdvisor',
    de: 'Alle Bewertungen auf TripAdvisor',
  },
  'home.tripadvisor.badge.rating': {
    en: '4.9 out of 5',
    de: '4,9 von 5',
  },
  'home.tripadvisor.badge.reviewCount': {
    en: '14 reviews',
    de: '14 Bewertungen',
  },
  'home.tourhq.sectionTitle': {
    en: 'Reviews on TourHQ',
    de: 'Aktuelle Bewertungen auf TourHQ',
  },
  'home.tourhq.cardText': {
    en: 'Read verified reviews and see Zuzana\'s full guide profile on TourHQ.',
    de: 'Lesen Sie verifizierte Bewertungen und sehen Sie Zuzanas vollständiges Guide-Profil auf TourHQ.',
  },
  'home.tourhq.viewProfile': {
    en: 'View profile on TourHQ',
    de: 'Profil auf TourHQ öffnen',
  },
  'home.section.story.title': {
    en: 'Prague, told with passion',
    de: 'Prag, erzählt mit Leidenschaft',
  },
  'home.gallery.title': {
    en: 'Moments from the city',
    de: 'Momente aus der Stadt',
  },
  'home.testimonials.title': {
    en: 'What our guests say',
    de: 'Was unsere Gäste sagen',
  },
  'home.testimonial1.quote': {
    en: "Zuzana took us to places we would never have found alone. Her stories made the stones come alive.",
    de: 'Zuzana führte uns an Orte, die wir alleine nie gefunden hätten. Ihre Geschichten haben die Steine lebendig gemacht.',
  },
  'home.testimonial1.author': {
    en: '— Guests from Germany',
    de: '— Gäste aus Deutschland',
  },
  'home.testimonial2.quote': {
    en: 'The perfect mix of history and local anecdotes. Her love for Prague is contagious.',
    de: 'Die perfekte Mischung aus Geschichte und lokalen Anekdoten. Ihre Liebe zu Prag ist ansteckend.',
  },
  'home.testimonial2.author': {
    en: '— Guests from the UK',
    de: '— Gäste aus dem UK',
  },
  'nav.about': {
    en: 'About',
    de: 'Über mich',
  },

  // Tours Page
  'tours.header.title': {
    en: 'Private Prague Tours',
    de: 'Private Stadtführungen Prag auf Deutsch',
  },
  'tours.header.subtitle': {
    en: 'Choose from my most popular tours or create a custom experience',
    de: 'Wählen Sie aus meinen beliebtesten Touren oder erstellen Sie gerne ein individuelles Erlebnis',
  },
  'tours.page.kicker': {
    en: 'Private tours · Prague',
    de: 'Private Stadtführungen Prag',
  },
  'tours.page.heading': {
    en: 'Private Prague tours',
    de: 'Prag auf Deutsch erleben',
  },
  'tours.page.lead': {
    en: 'Popular itineraries or a fully custom experience — built around what you want to see.',
    de: 'Beliebte Touren oder ein individuelles Erlebnis — ganz nach Ihren Wünschen',
  },
  'tours.section.popular': {
    en: 'Popular tours',
    de: 'Beliebte Touren',
  },
  'tours.section.custom': {
    en: 'Custom',
    de: 'Individuell',
  },
  'tours.badge.groupUpTo6': {
    en: 'Group up to 6',
    de: 'Gruppe bis 6',
  },
  'tours.badge.insider': {
    en: 'Insider route',
    de: 'Geheimtipps',
  },
  'tours.exploreTour': {
    en: 'Explore tour',
    de: 'Tour entdecken',
  },
  'tours.custom.requestCta': {
    en: 'Request private tour',
    de: 'Privattour anfragen',
  },
  'tours.custom.learnMore': {
    en: 'Learn more',
    de: 'Mehr erfahren',
  },
  'tours.stickyCta.hint': {
    en: 'Not sure yet? I am happy to help you choose in person.',
    de: 'Noch unsicher? Ich berate Sie gerne persönlich.',
  },
  'tours.stickyCta.button': {
    en: 'Contact',
    de: 'Kontakt',
  },

  // Tour 1: Castle
  'tour.castle.title': {
    en: 'Prague Castle & Royal District',
    de: 'Prager Burg & Königliches Viertel',
  },
  'tour.castle.description': {
    en: 'Explore the magnificent Prague Castle complex, St. Vitus Cathedral, and Golden Lane. Discover centuries of royal history in this UNESCO World Heritage site.',
    de: 'Erkunden Sie den prächtigen Prager Burgkomplex, den Veitsdom und das Goldene Gässchen. Entdecken Sie jahrhundertealte königliche Geschichte in diesem UNESCO-Weltkulturerbe.',
  },
  'tour.castle.duration': {
    en: '3-4 hours',
    de: '3-4 Stunden',
  },
  'tour.castle.h1': {
    en: 'Prague Castle courtyards',
    de: 'Höfe der Prager Burg',
  },
  'tour.castle.h2': {
    en: 'St. Vitus Cathedral',
    de: 'Veitsdom',
  },
  'tour.castle.h3': {
    en: 'Golden Lane',
    de: 'Goldenes Gässchen',
  },
  'tour.castle.h4': {
    en: 'Stunning panoramic views',
    de: 'Atemberaubende Panoramablicke',
  },

  // Tour 2: Old Town & Jewish Quarter
  'tour.oldtown.title': {
    en: 'Old Town & Jewish Quarter',
    de: 'Altstadt & Jüdisches Viertel',
  },
  'tour.oldtown.description': {
    en: 'Walk through the medieval streets of Old Town, visit the Astronomical Clock, and explore the historic Jewish Quarter with an expert certified guide.',
    de: 'Spazieren Sie durch die mittelalterlichen Gassen der Altstadt, besuchen Sie die Astronomische Uhr und erkunden Sie das historische Jüdische Viertel mit einer zertifizierten Expertin.',
  },
  'tour.oldtown.duration': {
    en: '3 hours',
    de: '3 Stunden',
  },
  'tour.oldtown.h1': {
    en: 'Old Town Square',
    de: 'Altstädter Ring',
  },
  'tour.oldtown.h2': {
    en: 'Astronomical Clock',
    de: 'Astronomische Uhr',
  },
  'tour.oldtown.h3': {
    en: 'Jewish Quarter',
    de: 'Jüdisches Viertel',
  },
  'tour.oldtown.h4': {
    en: 'Charles Bridge',
    de: 'Karlsbrücke',
  },

  // Tour 3: Hidden Prague
  'tour.hidden.title': {
    en: 'Hidden Prague',
    de: 'Verstecktes Prag',
  },
  'tour.hidden.description': {
    en: 'Discover secret gardens, hidden courtyards, and local spots that most tourists never find. Experience Prague like a local!',
    de: 'Entdecken Sie geheime Gärten, versteckte Höfe und lokale Orte, die die meisten Touristen nie finden. Erleben Sie Prag wie ein Einheimischer!',
  },
  'tour.hidden.duration': {
    en: '2-3 hours',
    de: '2-3 Stunden',
  },
  'tour.hidden.h1': {
    en: 'Secret gardens',
    de: 'Geheime Gärten',
  },
  'tour.hidden.h2': {
    en: 'Local cafes',
    de: 'Lokale Cafés',
  },
  'tour.hidden.h3': {
    en: 'Hidden courtyards',
    de: 'Versteckte Innenhöfe',
  },
  'tour.hidden.h4': {
    en: 'Off-the-beaten-path',
    de: 'Abseits der üblichen Wege',
  },

  // Tour 4: German Ties
  'tour.german.title': {
    en: 'Prague\'s German Heritage',
    de: 'Prags Deutsches Erbe',
  },
  'tour.german.description': {
    en: 'Explore Prague\'s rich German cultural heritage, from medieval times to modern history. A special tour highlighting German-Czech connections.',
    de: 'Erkunden Sie Prags reiches deutsches Kulturerbe, vom Mittelalter bis zur modernen Geschichte. Eine besondere Tour, die die deutsch-tschechischen Verbindungen hervorhebt.',
  },
  'tour.german.duration': {
    en: 'Flexible',
    de: 'Flexibel',
  },
  'tour.german.h1': {
    en: 'German-Czech history',
    de: 'Deutsch-tschechische Geschichte',
  },
  'tour.german.h2': {
    en: 'Cultural heritage sites',
    de: 'Kulturelle Erbestätten',
  },
  'tour.german.h3': {
    en: 'Historical connections',
    de: 'Historische Verbindungen',
  },
  'tour.german.h4': {
    en: 'Expert insights',
    de: 'Fachkundige Einblicke',
  },

  // Tour 5: Custom
  'tour.custom.title': {
    en: 'Custom Private Tour',
    de: 'Individuelle Privattour',
  },
  'tour.custom.description': {
    en: 'Create your own perfect Prague experience! Whether you want history, architecture, food, or a mix of everything, we\'ll design a tour just for you.',
    de: 'Gestalten Sie Ihr perfektes Prag-Erlebnis! Ob Geschichte, Architektur, Essen oder eine Mischung aus allem – wir entwerfen eine Tour speziell für Sie.',
  },
  'tour.custom.duration': {
    en: 'Flexible',
    de: 'Flexibel',
  },
  'tour.custom.h1': {
    en: 'Fully customizable',
    de: 'Vollständig anpassbar',
  },
  'tour.custom.h2': {
    en: 'Your pace, your interests',
    de: 'Ihr Tempo, Ihre Interessen',
  },
  'tour.custom.h3': {
    en: 'Private tour',
    de: 'Private Führung',
  },
  'tour.custom.h4': {
    en: 'Personalized recommendations',
    de: 'Personalisierte Empfehlungen',
  },

  // Tour 6: Václav Havel Tour
  'tour.havel.title': {
    en: 'Václav Havel Tour: Sites of Freedom',
    de: 'Václav-Havel-Tour: Orte der Freiheit',
  },
  'tour.havel.description': {
    en: "Walk through Prague's modern history following Václav Havel—playwright, dissident, and president. Visit key places of the Velvet Revolution, civic life, and Havel's cultural legacy.",
    de: 'Spazieren Sie durch Prags jüngere Geschichte auf den Spuren Václav Havels—Dramatiker, Dissident und Präsident. Besuchen Sie Schlüsselschauplätze der Samtenen Revolution, des zivilen Engagements und Havels kulturellen Erbes.',
  },
  'tour.havel.duration': {
    en: '2.5–3 hours',
    de: '2,5–3 Stunden',
  },
  'tour.havel.h1': {
    en: 'Velvet Revolution sites',
    de: 'Schauplätze der Samtenen Revolution',
  },
  'tour.havel.h2': {
    en: 'Wenceslas Square & Národní',
    de: 'Wenzelsplatz & Národní',
  },
  'tour.havel.h3': {
    en: 'Civic Forum HQ (Laterna magika)',
    de: 'Hauptquartier des Bürgerforums (Laterna magika)',
  },
  'tour.havel.h4': {
    en: 'Havel’s cultural legacy',
    de: 'Havels kulturelles Erbe',
  },
  'tour.havel.blogReadMore': {
    en: 'Read the full blog post',
    de: 'Zum vollständigen Beitrag',
  },
  'tour.havel.blogUrl': {
    en: '/blog/vaclav-havel-tour-prague',
    de: '/blog/vaclav-havel-tour-prague',
  },

  // ─── Tour subpage shared UI ────────────────────────────────────────────────
  'tourpage.included': { en: "What's included", de: 'Im Preis enthalten' },
  'tourpage.meetingPoint': { en: 'Meeting point', de: 'Treffpunkt' },
  'tourpage.duration': { en: 'Duration', de: 'Dauer' },
  'tourpage.language': { en: 'Language', de: 'Sprache' },
  'tourpage.languageValue': { en: 'German & English', de: 'Deutsch & Englisch' },
  'tourpage.groupSize': { en: 'Group size', de: 'Gruppengröße' },
  'tourpage.groupSizeValue': { en: 'Private (your group only)', de: 'Privat (nur Ihre Gruppe)' },
  'tourpage.faq': { en: 'Frequently asked questions', de: 'Häufig gestellte Fragen' },
  'tourpage.bookCta': { en: 'Book this tour', de: 'Diese Tour buchen' },
  'tourpage.enquiryCta': { en: 'Send an enquiry', de: 'Anfrage senden' },
  'tourpage.allTours': { en: '← All tours', de: '← Alle Touren' },
  'tourpage.highlights': { en: 'Highlights', de: 'Highlights' },

  // ─── Castle tour subpage ───────────────────────────────────────────────────
  'tour.castle.seoTitle': {
    en: 'Prague Castle Private Tour in German & English',
    de: 'Prager Burg – Private Stadtführung Prag auf Deutsch',
  },
  'tour.castle.body1': {
    en: 'Prague Castle is the largest ancient castle complex in the world — over 70,000 square metres of courtyards, palaces, churches, and gardens stacked on the hill above the city. It has been the seat of Bohemian kings, Holy Roman Emperors, and Czech presidents. If you only have time for one thing in Prague, this is it.',
    de: 'Die Prager Burg ist das größte alte Burgkomplex der Welt — über 70.000 Quadratmeter Innenhöfe, Paläste, Kirchen und Gärten auf dem Hügel über der Stadt. Sie war Sitz böhmischer Könige, römisch-deutscher Kaiser und tschechischer Präsidenten. Wenn Sie in Prag nur eine Sache sehen können, dann diese.',
  },
  'tour.castle.body2': {
    en: 'Most visitors walk through the castle in a rush, photograph the cathedral, and leave. A private tour is different. We take our time in the courtyards where coronation processions once assembled. We step inside St. Vitus Cathedral and I tell you exactly who is buried beneath your feet — seven Holy Roman Emperors, including Charles IV. We walk Golden Lane, where Franz Kafka briefly rented a tiny house to write in peace. And we find a viewpoint over the red rooftops of Malá Strana that almost nobody knows about.',
    de: 'Die meisten Besucher eilen durch die Burg, fotografieren den Dom und gehen wieder. Eine private Tour ist anders. Wir nehmen uns Zeit auf den Innenhöfen, wo einst Krönungszüge aufmarschierten. Wir betreten den Veitsdom, und ich erkläre Ihnen genau, wer unter Ihren Füßen begraben liegt — sieben römisch-deutsche Kaiser, darunter Karl IV. Wir gehen durch das Goldene Gässchen, wo Franz Kafka kurzzeitig ein winziges Häuschen mietete, um in Ruhe zu schreiben. Und wir finden einen Aussichtspunkt über die roten Dächer der Kleinseite, den fast niemand kennt.',
  },
  'tour.castle.body3': {
    en: 'I have been guiding at the castle since 1986 — before the Velvet Revolution, before the crowds arrived. I know which entrance avoids the longest queues, which hour of the day the light in the cathedral is extraordinary, and which stories do not make it into the guidebooks. Book the castle tour as a morning trip; we finish with coffee and a view that stays with you.',
    de: 'Ich führe an der Burg seit 1986 — vor der Samtenen Revolution, bevor die Massen kamen. Ich weiß, welcher Eingang die längsten Warteschlangen vermeidet, zu welcher Tageszeit das Licht im Dom außergewöhnlich ist und welche Geschichten es nicht in die Reiseführer schaffen. Buchen Sie die Burgtour als Morgenausflug; wir enden mit Kaffee und einem Blick, der bleibt.',
  },
  'tour.castle.meetingPoint': {
    en: 'First Courtyard gate, Hradčanské náměstí (Castle Square) — I will be waiting for you at the main gate.',
    de: 'Tor zum Ersten Burghof, Hradčanské náměstí (Burgplatz) — ich warte am Haupttor auf Sie.',
  },
  'tour.castle.inc1': { en: 'Personal certified guide (German & English)', de: 'Persönliche zertifizierte Führung (Deutsch & Englisch)' },
  'tour.castle.inc2': { en: 'Skip-the-queue entrance strategy', de: 'Eintrittsstrategie ohne lange Wartezeit' },
  'tour.castle.inc3': { en: 'St. Vitus Cathedral interior visit', de: 'Besuch des Veitsdom-Innenraums' },
  'tour.castle.inc4': { en: 'Golden Lane & hidden viewpoint', de: 'Goldenes Gässchen & versteckter Aussichtspunkt' },
  'tour.castle.faq1.q': { en: 'Do I need to book entry tickets in advance?', de: 'Muss ich Eintrittskarten im Voraus buchen?' },
  'tour.castle.faq1.a': { en: 'Advance ticket booking is recommended in peak season (April–October). I can advise you on the best ticket combination for your interests, and I know which entrances have shorter queues at different times of day.', de: 'In der Hauptsaison (April–Oktober) empfiehlt sich eine Vorabbuchung der Eintrittskarten. Ich berate Sie gern, welche Ticketkombination für Ihre Interessen am sinnvollsten ist, und kenne die Eingänge mit kürzeren Warteschlangen zu verschiedenen Tageszeiten.' },
  'tour.castle.faq2.q': { en: 'Is the castle accessible for visitors with limited mobility?', de: 'Ist die Burg für Besucher mit eingeschränkter Mobilität zugänglich?' },
  'tour.castle.faq2.a': { en: 'Parts of the castle complex are accessible, though the steep cobblestone approach from Malá Strana is challenging. The easiest access is by tram to Pohořelec or by car to Castle Square. Please mention any mobility needs when booking and I will plan the route accordingly.', de: 'Teile des Burgkomplexes sind barrierefrei zugänglich, jedoch ist der steile Kopfsteinpflasterweg von der Kleinseite anspruchsvoll. Der einfachste Zugang ist per Straßenbahn bis Pohořelec oder mit dem Auto zum Burgplatz. Bitte teilen Sie etwaige Mobilitätsbedürfnisse bei der Buchung mit — ich plane die Route entsprechend.' },
  'tour.castle.faq3.q': { en: 'How long does the tour take?', de: 'Wie lange dauert die Tour?' },
  'tour.castle.faq3.a': { en: 'Typically 3 to 4 hours depending on your interests and pace. We never rush. If you want to linger in the cathedral or spend extra time in Golden Lane, we do that.', de: 'In der Regel 3 bis 4 Stunden, je nach Interessen und Tempo. Wir hetzen nie. Wenn Sie im Dom verweilen oder mehr Zeit im Goldenen Gässchen verbringen möchten, machen wir das.' },
  'tour.castle.faq4.q': { en: 'Can we combine the castle tour with Old Town on the same day?', de: 'Können wir die Burgtour am selben Tag mit der Altstadt kombinieren?' },
  'tour.castle.faq4.a': { en: 'Yes — many visitors do a half-day castle tour in the morning and Old Town in the afternoon, with a break for lunch. It makes for a full but very rewarding day. I can arrange both tours back to back.', de: 'Ja — viele Besucher machen morgens eine halbtägige Burgtour und nachmittags die Altstadt, mit einer Mittagspause. Das ergibt einen vollen, aber sehr lohnenden Tag. Ich kann beide Touren hintereinander arrangieren.' },

  // ─── Old Town & Jewish Quarter subpage ────────────────────────────────────
  'tour.oldtown.seoTitle': {
    en: 'Old Town & Jewish Quarter Private Tour in German & English',
    de: 'Altstadt & Jüdisches Viertel – Prag Privatführung auf Deutsch',
  },
  'tour.oldtown.body1': {
    en: "Prague's Old Town is a medieval city that survived into the modern era largely intact — which makes it extraordinary. The streets are genuinely old: the same lanes Kafka walked to school, the same square where Bohemian kings were crowned and executions were held, the same astronomical clock that has been marking the hours since 1410. Walking it without context is pleasant. Walking it with a guide who has spent 40 years studying it is something else entirely.",
    de: 'Prags Altstadt ist eine mittelalterliche Stadt, die weitgehend unversehrt in die Moderne überlebt hat — das macht sie außergewöhnlich. Die Straßen sind wirklich alt: dieselben Gassen, durch die Kafka zur Schule ging, derselbe Platz, auf dem böhmische Könige gekrönt und Hinrichtungen vollzogen wurden, dieselbe Astronomische Uhr, die seit 1410 die Stunden schlägt. Ohne Hintergrundwissen durch die Altstadt zu laufen ist angenehm. Mit einer Führerin, die 40 Jahre damit verbracht hat, sie zu erforschen, ist es etwas völlig anderes.',
  },
  'tour.oldtown.body2': {
    en: 'The Jewish Quarter — Josefov — is one of the most significant Jewish heritage sites in Europe. Six synagogues, the Old Jewish Cemetery with its layers of graves stacked on top of each other for centuries, and stories of the community that shaped the cultural life of Prague for hundreds of years. I am accredited by the Jewish Museum in Prague specifically to guide here, which means I can take you inside sites and share context that most guides cannot.',
    de: 'Das Jüdische Viertel — Josefov — ist eine der bedeutendsten jüdischen Kulturstätten Europas. Sechs Synagogen, der Alte Jüdische Friedhof mit seinen seit Jahrhunderten übereinander gestapelten Grabschichten, und Geschichten einer Gemeinschaft, die das kulturelle Leben Prags über Jahrhunderte geprägt hat. Ich bin speziell vom Jüdischen Museum Prag akkreditiert, um hier Führungen durchzuführen — das bedeutet, ich kann Sie in Stätten mitnehmen und Zusammenhänge vermitteln, die die meisten Guides nicht können.',
  },
  'tour.oldtown.body3': {
    en: 'We finish at or near Charles Bridge, where I will point out the medieval towers, tell you which of the 30 saint statues is which, and explain why the bridge is technically older than most people think. The tour can be done in the morning when the Old Town Square is calm, or in the early evening when the light on the facades turns golden. Both are worth it for different reasons.',
    de: 'Wir beenden die Tour an oder nahe der Karlsbrücke, wo ich Ihnen die mittelalterlichen Türme zeige, erkläre, welche der 30 Heiligenstatuen wer ist, und warum die Brücke technisch gesehen älter ist, als die meisten denken. Die Tour lässt sich morgens machen, wenn der Altstädter Ring ruhig ist, oder früh abends, wenn das Licht auf den Fassaden golden wird. Beides lohnt sich aus verschiedenen Gründen.',
  },
  'tour.oldtown.meetingPoint': {
    en: 'Jan Hus Monument, Old Town Square — I will be standing by the monument.',
    de: 'Jan-Hus-Denkmal, Altstädter Ring — ich stehe beim Denkmal.',
  },
  'tour.oldtown.inc1': { en: 'Personal certified guide (German & English)', de: 'Persönliche zertifizierte Führung (Deutsch & Englisch)' },
  'tour.oldtown.inc2': { en: 'Jewish Museum accreditation — specialist insight', de: 'Jüdisches Museum Akkreditierung — Fachkenntnisse' },
  'tour.oldtown.inc3': { en: 'Old Town Square & Astronomical Clock', de: 'Altstädter Ring & Astronomische Uhr' },
  'tour.oldtown.inc4': { en: 'Charles Bridge with historical commentary', de: 'Karlsbrücke mit historischem Kommentar' },
  'tour.oldtown.faq1.q': { en: 'Does the tour include entry to the Jewish Museum?', de: 'Ist der Eintritt ins Jüdische Museum im Preis enthalten?' },
  'tour.oldtown.faq1.a': { en: 'Entry tickets to the Jewish Museum sites are not included in the tour fee but I will advise you on the best ticket to buy. Tickets can be purchased on the day or in advance — I recommend advance booking in summer.', de: 'Eintrittskarten für die Stätten des Jüdischen Museums sind nicht im Tourpreis enthalten, aber ich berate Sie, welches Ticket am sinnvollsten ist. Karten können am Tag selbst oder im Voraus gekauft werden — im Sommer empfehle ich Vorabbuchung.' },
  'tour.oldtown.faq2.q': { en: 'Is there a lot of walking? My elderly parent is joining us.', de: 'Gibt es viel zu Fuß? Mein älterer Elternteil kommt mit.' },
  'tour.oldtown.faq2.a': { en: 'The Old Town is largely flat and very walkable. The Jewish Quarter is entirely flat. The only moderate incline is the approach to Charles Bridge from the Old Town side, which is gentle. This tour is suitable for most fitness levels. Just let me know when booking and I will adjust the pace.', de: 'Die Altstadt ist weitgehend flach und sehr gut zu Fuß erkunden. Das Jüdische Viertel ist völlig eben. Die einzige moderate Steigung ist der Zugang zur Karlsbrücke von der Altstädter Seite, der sanft ist. Diese Tour ist für die meisten Fitnessniveaus geeignet. Teilen Sie mir das bei der Buchung mit, und ich passe das Tempo an.' },
  'tour.oldtown.faq3.q': { en: 'Can we include the Astronomical Clock striking the hour?', de: 'Können wir die Astronomische Uhr beim Stundenschlag einschließen?' },
  'tour.oldtown.faq3.a': { en: 'Yes, I always plan the route so we arrive at the clock at a full or half hour if possible. The mechanism is beautiful to watch. I will explain what each figure means and why the clock shows three different calendar systems simultaneously.', de: 'Ja, ich plane die Route immer so, dass wir wenn möglich zur vollen oder halben Stunde an der Uhr ankommen. Der Mechanismus ist wunderschön anzusehen. Ich erkläre, was jede Figur bedeutet und warum die Uhr gleichzeitig drei verschiedene Kalender anzeigt.' },
  'tour.oldtown.faq4.q': { en: 'What is the best time of day for this tour?', de: 'Was ist die beste Tageszeit für diese Tour?' },
  'tour.oldtown.faq4.a': { en: 'Early morning (8–10am) means fewer crowds and beautiful soft light. Late afternoon (4–6pm) gives golden light on the facades and a more relaxed atmosphere as day-trippers leave. Both are excellent; it depends whether you prefer a calm start or a golden-hour finish.', de: 'Am frühen Morgen (8–10 Uhr) gibt es weniger Menschenmassen und schönes weiches Licht. Am späten Nachmittag (16–18 Uhr) fällt goldenes Licht auf die Fassaden und die Atmosphäre ist entspannter, wenn die Tagesausflügler gehen. Beides ist ausgezeichnet — es kommt darauf an, ob Sie einen ruhigen Beginn oder ein Goldstunden-Finale bevorzugen.' },

  // ─── Custom Private Tour subpage ──────────────────────────────────────────
  'tour.custom.seoTitle': {
    en: 'Custom Private Prague Tour — Tailored to You',
    de: 'Individuelle Privattour Prag – maßgeschneiderte Stadtführung auf Deutsch',
  },
  'tour.custom.body1': {
    en: 'Every group that walks Prague with me is different. Some want pure history — the medieval layers, the Habsburg centuries, the scars of the 20th century. Some want architecture and want to stop in front of every Art Nouveau doorway. Some have children and need the pace to breathe, with stops at gardens and playgrounds woven into the route. Some want a slow morning with coffee breaks. Some want to cover as much as possible in four hours.',
    de: 'Jede Gruppe, die Prag mit mir erkundet, ist anders. Manche wollen pure Geschichte — die mittelalterlichen Schichten, die habsburgischen Jahrhunderte, die Narben des 20. Jahrhunderts. Manche wollen Architektur und möchten vor jedem Jugendstil-Eingang stehen bleiben. Manche haben Kinder und brauchen Luft zum Atmen, mit Haltepunkten in Gärten entlang der Route. Manche wollen einen gemütlichen Morgen mit Kaffeepausen. Manche möchten in vier Stunden so viel wie möglich abdecken.',
  },
  'tour.custom.body2': {
    en: 'A custom private tour means we design it together before you arrive. You tell me what you are most curious about — I send back a suggested route and we adjust it until it feels right. On the day, if something catches your eye or you want to spend longer somewhere, we change the plan. That is the point of a private tour. The route serves you, not the other way around.',
    de: 'Eine individuelle Privattour bedeutet, dass wir sie gemeinsam gestalten, bevor Sie ankommen. Sie sagen mir, was Sie am meisten interessiert — ich schicke Ihnen eine Routenempfehlung, und wir passen sie an, bis sie sich richtig anfühlt. Am Tag selbst, wenn Ihnen etwas auffällt oder Sie irgendwo länger bleiben möchten, ändern wir den Plan. Das ist der Sinn einer Privattour. Die Route dient Ihnen, nicht umgekehrt.',
  },
  'tour.custom.body3': {
    en: 'Popular starting points for custom tours: the view from Letná Park down over the bend in the river, the quiet Baroque gardens below the castle, the covered passage through the Lucerna arcade in Wenceslas Square, or a morning at Náplavka market on the riverbank on Saturdays. I have been collecting these places for 40 years. The custom tour is how I share the ones that never make the guidebooks.',
    de: 'Beliebte Ausgangspunkte für individuelle Touren: der Blick vom Letná-Park über die Flussschleife, die ruhigen Barockgärten unterhalb der Burg, der überdachte Gang durch die Lucerna-Passage am Wenzelsplatz oder ein Morgen auf dem Náplavka-Markt am Flussufer samstags. Ich sammle diese Orte seit 40 Jahren. Die individuelle Tour ist meine Art, die zu teilen, die es nie in die Reiseführer schaffen.',
  },
  'tour.custom.meetingPoint': {
    en: 'We agree the meeting point together when planning your tour — usually your hotel or a central landmark of your choice.',
    de: 'Den Treffpunkt vereinbaren wir gemeinsam bei der Planung Ihrer Tour — in der Regel Ihr Hotel oder ein zentrales Wahrzeichen Ihrer Wahl.',
  },
  'tour.custom.inc1': { en: 'Personalised pre-tour route planning', de: 'Persönliche Routenplanung vor der Tour' },
  'tour.custom.inc2': { en: 'Private guide — your group only', de: 'Privater Guide — nur Ihre Gruppe' },
  'tour.custom.inc3': { en: 'Flexible duration and pace', de: 'Flexible Dauer und Tempo' },
  'tour.custom.inc4': { en: 'Recommendations for lunch, dinner & local spots', de: 'Empfehlungen für Mittagessen, Abendessen & lokale Orte' },
  'tour.custom.faq1.q': { en: 'How far in advance should I book?', de: 'Wie weit im Voraus sollte ich buchen?' },
  'tour.custom.faq1.a': { en: 'A week or more in advance is ideal so we have time to plan the route together. That said, I can often accommodate last-minute requests — just contact me and I will let you know my availability.', de: 'Eine Woche oder mehr im Voraus ist ideal, damit wir Zeit haben, die Route gemeinsam zu planen. Dennoch kann ich oft auch kurzfristige Anfragen berücksichtigen — kontaktieren Sie mich einfach und ich teile Ihnen meine Verfügbarkeit mit.' },
  'tour.custom.faq2.q': { en: 'We are a family with young children. Is this suitable?', de: 'Wir sind eine Familie mit kleinen Kindern. Ist das geeignet?' },
  'tour.custom.faq2.a': { en: 'Absolutely. Family tours are some of my favourites. I know which stories capture children\'s imaginations, which gardens have good open space for a break, and how to keep the pace comfortable for everyone. Just tell me the ages when you get in touch.', de: 'Absolut. Familientouren gehören zu meinen liebsten. Ich weiß, welche Geschichten die Vorstellungskraft von Kindern anregen, welche Gärten guten Freiraum für eine Pause bieten und wie man das Tempo für alle angenehm hält. Teilen Sie mir beim Kontakt einfach die Altersgruppen mit.' },
  'tour.custom.faq3.q': { en: 'Can we focus on a specific topic — architecture, food, Kafka?', de: 'Können wir uns auf ein bestimmtes Thema konzentrieren — Architektur, Essen, Kafka?' },
  'tour.custom.faq3.a': { en: 'Yes, that is exactly the point of a custom tour. Thematic tours — Art Nouveau architecture, Jewish heritage, Kafka\'s Prague, Communist-era buildings, the Velvet Revolution — are often the most memorable because there is a thread running through the whole day.', de: 'Ja, genau das ist der Sinn einer individuellen Tour. Thematische Touren — Jugendstilarchitektur, jüdisches Erbe, Kafkas Prag, Gebäude aus der kommunistischen Ära, die Samtene Revolution — sind oft die einprägsamsten, weil ein roter Faden durch den gesamten Tag führt.' },
  'tour.custom.faq4.q': { en: 'What is the pricing for a custom tour?', de: 'Was kostet eine individuelle Tour?' },
  'tour.custom.faq4.a': { en: 'Pricing depends on duration and group size. Contact me with your dates, group size, and preferred duration and I will send you a quote within 24 hours.', de: 'Der Preis hängt von Dauer und Gruppengröße ab. Kontaktieren Sie mich mit Ihren Daten, Gruppengröße und gewünschter Dauer, und ich schicke Ihnen innerhalb von 24 Stunden ein Angebot.' },

  // ─── Hidden Prague subpage ─────────────────────────────────────────────────
  'tour.hidden.seoTitle': {
    en: 'Hidden Prague Private Tour — Secret Spots & Local Gems',
    de: 'Verstecktes Prag – Private Führung abseits der Touristenpfade',
  },
  'tour.hidden.body1': {
    en: 'Every city has two versions of itself: the one in the photographs and the one the locals know. In Prague the gap between those two is unusually large. The Charles Bridge photograph is real, but it is not the whole picture. There are courtyards in the Old Town that have been private for 600 years and are now, quietly, open. There are gardens behind the castle walls that almost no visitor finds. There is a small Baroque street in Malá Strana that looks exactly like it did in the 18th century.',
    de: 'Jede Stadt hat zwei Versionen von sich: die, die auf den Fotos zu sehen ist, und die, die die Einheimischen kennen. In Prag ist der Unterschied zwischen diesen beiden ungewöhnlich groß. Das Karlsbrücken-Foto ist real, aber es ist nicht das ganze Bild. Es gibt Innenhöfe in der Altstadt, die 600 Jahre lang privat waren und nun still und leise geöffnet sind. Es gibt Gärten hinter den Burgmauern, die fast kein Besucher findet. Es gibt eine kleine Barockgasse in der Kleinseite, die genauso aussieht wie im 18. Jahrhundert.',
  },
  'tour.hidden.body2': {
    en: "This tour has no fixed route. I build it around the city's current state — what is open, what is blooming, what unexpected thing is happening in a particular courtyard or square on the day you visit. What stays constant: we leave the main tourist path within the first ten minutes, and we rarely return to it. The goal is not to see Prague — it is to feel it.",
    de: 'Diese Tour hat keine feste Route. Ich gestalte sie nach dem aktuellen Zustand der Stadt — was geöffnet ist, was blüht, was sich an einem bestimmten Hof oder Platz an dem Tag, an dem Sie besuchen, Unerwartetes ereignet. Was konstant bleibt: Wir verlassen den touristischen Hauptweg innerhalb der ersten zehn Minuten, und kehren selten zurück. Das Ziel ist nicht, Prag zu sehen — sondern es zu spüren.',
  },
  'tour.hidden.body3': {
    en: 'Previous tours have included: a Romanesque rotunda from the 12th century hidden in plain sight behind a petrol station; a roof terrace above a Baroque library with a view nobody photographs because nobody knows it is there; a small square in Vinohrady where locals play boules on Tuesday evenings; a passage through four connected courtyards that cuts invisibly through two city blocks. None of these are secrets exactly. They just require someone who knows where to look.',
    de: 'Frühere Touren haben umfasst: eine romanische Rotunde aus dem 12. Jahrhundert, die unbemerkt hinter einer Tankstelle versteckt ist; eine Dachterrasse über einer Barockbibliothek mit einem Ausblick, den niemand fotografiert, weil niemand weiß, dass sie da ist; ein kleiner Platz in Vinohrady, wo Einheimische dienstags abends Boule spielen; eine Passage durch vier verbundene Innenhöfe, die unsichtbar durch zwei Häuserblocks verläuft. Das sind eigentlich keine Geheimnisse. Man muss nur wissen, wo man suchen muss.',
  },
  'tour.hidden.meetingPoint': {
    en: 'Agreed when booking — I choose the starting point based on which neighbourhood has the most interesting things open on your day.',
    de: 'Bei der Buchung vereinbart — ich wähle den Ausgangspunkt nach dem Stadtteil, in dem an Ihrem Tag am meisten Interessantes geöffnet ist.',
  },
  'tour.hidden.inc1': { en: 'Off-the-beaten-path route, different every time', de: 'Abseits der Touristenpfade, jedes Mal anders' },
  'tour.hidden.inc2': { en: 'Access to hidden courtyards and private passages', de: 'Zugang zu versteckten Höfen und privaten Durchgängen' },
  'tour.hidden.inc3': { en: 'Local cafe or pastry stop included', de: 'Café oder Gebäckstopp bei Einheimischen inbegriffen' },
  'tour.hidden.inc4': { en: 'Personal guide who has been collecting these spots since 1986', de: 'Persönlicher Guide, der diese Orte seit 1986 sammelt' },
  'tour.hidden.faq1.q': { en: 'How is this different from a regular walking tour?', de: 'Was unterscheidet diese Tour von einer regulären Stadtführung?' },
  'tour.hidden.faq1.a': { en: "A regular tour covers the landmarks every visitor sees. This one deliberately avoids them. We might walk past the Astronomical Clock but we will not stop to explain it — we are heading somewhere more interesting. If you have already seen Prague's highlights, or if you simply prefer the road less travelled, this tour is for you.", de: "Eine reguläre Tour deckt die Sehenswürdigkeiten ab, die jeder Besucher sieht. Diese weicht ihnen absichtlich aus. Wir könnten an der Astronomischen Uhr vorbeigehen, aber wir halten nicht an — wir sind auf dem Weg zu etwas Interessanterem. Wenn Sie Prags Highlights bereits kennen oder den weniger begangenen Weg bevorzugen, ist diese Tour für Sie." },
  'tour.hidden.faq2.q': { en: 'Will we really avoid the tourist crowds?', de: 'Werden wir die Touristenströme wirklich meiden?' },
  'tour.hidden.faq2.a': { en: 'Mostly, yes. We will cross the Old Town but not linger in it. Some of the places I take you are genuinely off the tourist map — small enough, obscure enough, that even on busy summer days they are quiet.', de: 'Größtenteils ja. Wir werden die Altstadt durchqueren, aber nicht dort verweilen. Einige der Orte, zu denen ich Sie führe, sind tatsächlich abseits der Touristenkarte — klein und unbekannt genug, sodass sie selbst an belebten Sommertagen ruhig sind.' },
  'tour.hidden.faq3.q': { en: 'Can I combine this with the Castle or Old Town tour?', de: 'Kann ich diese Tour mit der Burg- oder Altstadttour kombinieren?' },
  'tour.hidden.faq3.a': { en: "Yes, but I would suggest doing the Hidden Prague tour on a different day. It works best when you already have a feel for the city's main landmarks — the hidden places make more sense when you have the reference points.", de: 'Ja, aber ich würde empfehlen, die Tour Verstecktes Prag an einem anderen Tag zu machen. Sie funktioniert am besten, wenn Sie bereits ein Gefühl für die wichtigsten Sehenswürdigkeiten haben — die verborgenen Orte ergeben mehr Sinn, wenn man die Bezugspunkte kennt.' },
  'tour.hidden.faq4.q': { en: 'Is this tour suitable for children?', de: 'Ist diese Tour für Kinder geeignet?' },
  'tour.hidden.faq4.a': { en: 'Yes — children often enjoy it more than standard tours because there are more surprises and fewer crowds. I adjust the stories for the audience. A courtyard with a mysterious old well is much more interesting to a ten-year-old than a list of dates.', de: 'Ja — Kinder genießen es oft mehr als Standardtouren, weil es mehr Überraschungen und weniger Menschenmassen gibt. Ich passe die Geschichten an das Publikum an. Ein Hof mit einem geheimnisvollen alten Brunnen ist für einen Zehnjährigen viel interessanter als eine Liste von Jahreszahlen.' },

  // ─── German Heritage Tour subpage ─────────────────────────────────────────
  'tour.german.seoTitle': {
    en: 'Prague German Heritage Private Tour',
    de: 'Prag Deutsches Erbe – Privatführung auf Deutsch',
  },
  'tour.german.body1': {
    en: 'For 700 years, Prague was one of the great German-speaking cities of Central Europe. German was the language of the court, of commerce, of the university that Charles IV founded in 1348 — the first in the Holy Roman Empire north of the Alps. Kafka wrote in German. Rilke studied here. Mozart premiered Don Giovanni in Prague because the city understood him in a way Vienna did not.',
    de: 'Siebenhundert Jahre lang war Prag eine der großen deutschsprachigen Städte Mitteleuropas. Deutsch war die Sprache des Hofes, des Handels, der Universität, die Karl IV. 1348 gründete — die erste im Heiligen Römischen Reich nördlich der Alpen. Kafka schrieb auf Deutsch. Rilke studierte hier. Mozart uraufführte Don Giovanni in Prag, weil die Stadt ihn auf eine Art verstand, wie es Wien nicht tat.',
  },
  'tour.german.body2': {
    en: 'This tour traces the German-Czech entanglement that shaped Prague — the cathedrals and palaces built by German-speaking rulers, the Jewish community that navigated between German and Czech culture for centuries, the complicated 20th century that ended with the expulsion of the Sudeten Germans and left a silence in the city that is only now slowly being acknowledged.',
    de: 'Diese Tour verfolgt die deutsch-tschechische Verflechtung, die Prag geprägt hat — die Kathedralen und Paläste, die von deutschsprachigen Herrschern errichtet wurden, die jüdische Gemeinschaft, die jahrhundertelang zwischen deutscher und tschechischer Kultur navigierte, das komplizierte 20. Jahrhundert, das mit der Vertreibung der Sudetendeutschen endete und eine Stille hinterließ, die erst jetzt langsam anerkannt wird.',
  },
  'tour.german.body3': {
    en: 'As a Czech guide who speaks German fluently and has studied this history for decades, I can give you a perspective you will not get from a guidebook — neither the German-nostalgia version nor the Czech-nationalist version, but something more honest and more interesting. This tour suits visitors who want to understand Central Europe, not just photograph it.',
    de: 'Als tschechische Führerin, die fließend Deutsch spricht und diese Geschichte seit Jahrzehnten studiert, kann ich Ihnen eine Perspektive bieten, die Sie in keinem Reiseführer finden — weder die deutsch-nostalgische noch die tschechisch-nationalistische Version, sondern etwas Ehrlicheres und Interessanteres. Diese Tour eignet sich für Besucher, die Mitteleuropa verstehen, nicht nur fotografieren möchten.',
  },
  'tour.german.meetingPoint': {
    en: 'Charles University, Old Town — Ovocný trh square, in front of the Carolinum building.',
    de: 'Karlsuniversität, Altstadt — Ovocný-trh-Platz, vor dem Carolinum-Gebäude.',
  },
  'tour.german.inc1': { en: 'Czech-German historical context from a bilingual expert', de: 'Deutsch-tschechischer historischer Kontext von einer zweisprachigen Expertin' },
  'tour.german.inc2': { en: 'Literary Prague: Kafka, Rilke, and Mozart\'s Prague', de: 'Literarisches Prag: Kafka, Rilke und Mozarts Prag' },
  'tour.german.inc3': { en: 'German-speaking rulers and the architecture they built', de: 'Deutschsprachige Herrscher und die Architektur, die sie bauten' },
  'tour.german.inc4': { en: 'Honest account of 20th-century German-Czech history', de: 'Ehrliche Darstellung der deutsch-tschechischen Geschichte des 20. Jahrhunderts' },
  'tour.german.faq1.q': { en: 'Is this tour only for German-speaking visitors?', de: 'Ist diese Tour nur für deutschsprachige Besucher?' },
  'tour.german.faq1.a': { en: 'No — I conduct it in German or English or both, depending on your group. The tour focuses on German cultural heritage in Prague, but the audience can be anyone with an interest in Central European history.', de: 'Nein — ich führe sie auf Deutsch, Englisch oder beidem durch, je nach Ihrer Gruppe. Die Tour konzentriert sich auf das deutsche Kulturerbe in Prag, aber das Publikum kann jeder sein, der an mitteleuropäischer Geschichte interessiert ist.' },
  'tour.german.faq2.q': { en: 'How much does the tour cover about the Sudeten German expulsions?', de: 'Wie viel behandelt die Tour die Vertreibung der Sudetendeutschen?' },
  'tour.german.faq2.a': { en: "I treat it seriously and honestly. The expulsions of 1945–46 are a significant part of Prague's 20th-century history and I do not skip them. I also give context — the occupation, the war — so the full picture is there. It is a tour for people who want to understand, not one that simplifies or avoids difficult history.", de: 'Ich behandle es ernsthaft und ehrlich. Die Vertreibungen von 1945–46 sind ein bedeutender Teil der Prager Geschichte des 20. Jahrhunderts, und ich lasse sie nicht aus. Ich gebe auch Kontext — die Besatzung, den Krieg — damit das vollständige Bild entsteht. Es ist eine Tour für Menschen, die verstehen wollen, keine, die schwierige Geschichte vereinfacht oder vermeidet.' },
  'tour.german.faq3.q': { en: 'Will we visit the places connected to Kafka?', de: 'Besuchen wir die mit Kafka verbundenen Orte?' },
  'tour.german.faq3.a': { en: "Yes — Kafka's Prague is central to this tour. We visit the house where he was born, the routes he walked between home and office, and the Jewish Quarter where his family lived. If you want a full day dedicated entirely to Kafka, I also run a standalone Kafka tour.", de: 'Ja — Kafkas Prag ist zentral für diese Tour. Wir besuchen das Haus, in dem er geboren wurde, die Wege zwischen Haus und Büro, und das Jüdische Viertel, wo seine Familie lebte. Wenn Sie einen ganzen Tag möchten, der ausschließlich Kafka gewidmet ist, führe ich auch eine eigenständige Kafka-Tour durch.' },
  'tour.german.faq4.q': { en: 'How long is this tour?', de: 'Wie lange dauert diese Tour?' },
  'tour.german.faq4.a': { en: 'Typically 2.5 to 3 hours, though this is one of the tours that often runs longer because the conversations go deep. We can agree a fixed end time if you have other plans.', de: 'In der Regel 2,5 bis 3 Stunden, obwohl dies eine der Touren ist, die oft länger dauern, weil die Gespräche tief gehen. Wir können eine feste Endzeit vereinbaren, wenn Sie andere Pläne haben.' },

  // ─── Havel Tour subpage ────────────────────────────────────────────────────
  'tour.havel.seoTitle': {
    en: 'Václav Havel Tour Prague — Velvet Revolution Private Tour',
    de: 'Václav-Havel-Tour Prag – Private Stadtführung Samtene Revolution',
  },
  'tour.havel.body1': {
    en: "Václav Havel was a playwright who became a dissident who became a president. His story runs through almost every major site in Prague's modern history — and it is one of the most extraordinary stories of the 20th century. The Velvet Revolution of 1989 ended 40 years of Communist rule in eleven days, without a shot fired, led in part by a man who had spent years under house arrest writing essays and plays that the regime tried to suppress.",
    de: 'Václav Havel war ein Dramatiker, der zum Dissidenten wurde, der zum Präsidenten wurde. Seine Geschichte zieht sich durch fast jeden bedeutenden Ort der modernen Prager Geschichte — und sie ist eine der außergewöhnlichsten des 20. Jahrhunderts. Die Samtene Revolution von 1989 beendete 40 Jahre kommunistischer Herrschaft in elf Tagen, ohne einen Schuss, angeführt von einem Mann, der jahrelang unter Hausarrest verbracht hatte und Essays und Theaterstücke schrieb, die das Regime zu unterdrücken versuchte.',
  },
  'tour.havel.body2': {
    en: 'I lived through November 1989 in Prague. I was there. The tour is not just academic — I can tell you what it felt like to stand on Wenceslas Square when half a million people rang their keys, what the city sounded like, what people said to each other. This is the version of the Velvet Revolution that does not exist in textbooks.',
    de: 'Ich habe den November 1989 in Prag erlebt. Ich war dabei. Die Tour ist nicht nur akademisch — ich kann Ihnen erzählen, wie es sich anfühlte, auf dem Wenzelsplatz zu stehen, als eine halbe Million Menschen ihre Schlüssel läuteten, wie die Stadt klang, was die Menschen zueinander sagten. Das ist die Version der Samtenen Revolution, die es in keinem Lehrbuch gibt.',
  },
  'tour.havel.body3': {
    en: "We visit Wenceslas Square, the spot on Národní where students were beaten on November 17th, the Laterna Magika theatre that became Civic Forum headquarters, and Havel's final apartment in the castle district. I also take you to Lucerna, the Havel family's cultural complex — a beautiful Art Nouveau passage that most visitors walk past without realising the building was designed by Havel's grandfather.",
    de: "Wir besuchen den Wenzelsplatz, den Ort auf der Národní, wo Studenten am 17. November geschlagen wurden, das Theater Laterna Magika, das Hauptquartier des Bürgerforums wurde, und Havels letzte Wohnung im Burgviertel. Ich nehme Sie auch ins Lucerna mit, dem Kulturkomplex der Familie Havel — eine wunderschöne Jugendstilpassage, an der die meisten Besucher vorbeigehen, ohne zu merken, dass das Gebäude von Havels Großvater entworfen wurde.",
  },
  'tour.havel.meetingPoint': {
    en: 'Top of Wenceslas Square, in front of the National Museum steps.',
    de: 'Oberes Ende des Wenzelsplatzes, vor den Stufen des Nationalmuseums.',
  },
  'tour.havel.inc1': { en: 'Eyewitness account of November 1989 from your guide', de: 'Augenzeugenbericht vom November 1989 durch Ihre Führerin' },
  'tour.havel.inc2': { en: 'Wenceslas Square, Národní, Laterna Magika', de: 'Wenzelsplatz, Národní, Laterna Magika' },
  'tour.havel.inc3': { en: 'Lucerna passage & Havel family history', de: 'Lucerna-Passage & Geschichte der Familie Havel' },
  'tour.havel.inc4': { en: 'Communist-era Prague and life under the regime', de: 'Prag zur Kommunistenzeit und das Leben unter dem Regime' },
  'tour.havel.faq1.q': { en: 'Do I need to know Czech history to enjoy this tour?', de: 'Muss ich tschechische Geschichte kennen, um diese Tour zu genießen?' },
  'tour.havel.faq1.a': { en: "Not at all. I build the context as we go — the tour works equally well for someone who knows Havel's writing intimately and someone who has only a vague idea that something happened in 1989. By the end, everyone has the full picture.", de: 'Überhaupt nicht. Ich baue den Kontext während der Tour auf — sie funktioniert gleich gut für jemanden, der Havels Werk gut kennt, und für jemanden, der nur eine vage Vorstellung hat, dass 1989 etwas geschah. Am Ende hat jeder das vollständige Bild.' },
  'tour.havel.faq2.q': { en: 'You were really there in 1989?', de: 'Sie waren wirklich 1989 dabei?' },
  'tour.havel.faq2.a': { en: 'Yes. I was living and working in Prague in November 1989. I stood on Wenceslas Square during the demonstrations. What I share on the tour is personal memory, not just historical record — and people often tell me that is what makes the difference.', de: 'Ja. Ich lebte und arbeitete im November 1989 in Prag. Ich stand während der Demonstrationen auf dem Wenzelsplatz. Was ich auf der Tour teile, ist persönliche Erinnerung, nicht nur historisches Zeugnis — und die Besucher sagen mir oft, dass das den Unterschied ausmacht.' },
  'tour.havel.faq3.q': { en: 'Can we combine this with the Old Town tour?', de: 'Können wir diese Tour mit der Altstadttour kombinieren?' },
  'tour.havel.faq3.a': { en: 'Yes — the tours overlap geographically but not thematically. The Old Town tour covers medieval and Jewish history; this one focuses on the 20th century. On the same day, I would suggest Old Town in the morning and Havel in the afternoon, finishing with a drink at a wine bar Havel frequented.', de: 'Ja — die Touren überschneiden sich geografisch, aber nicht thematisch. Die Altstadttour behandelt mittelalterliche und jüdische Geschichte; diese konzentriert sich auf das 20. Jahrhundert. Am selben Tag würde ich die Altstadt morgens und Havel nachmittags empfehlen, mit einem Abschlussgetränk in einer Weinbar, die Havel frequentierte.' },
  'tour.havel.faq4.q': { en: 'Is there a related blog post I can read before the tour?', de: 'Gibt es einen Blog-Beitrag, den ich vor der Tour lesen kann?' },
  'tour.havel.faq4.a': { en: "Yes — I have written a detailed piece on Havel's Prague with the key sites and their stories. You can read it on the blog before your visit to get more out of the tour.", de: 'Ja — ich habe einen detaillierten Artikel über Havels Prag mit den wichtigsten Stätten und ihren Geschichten geschrieben. Sie können ihn vor Ihrem Besuch im Blog lesen, um mehr aus der Tour herauszuholen.' },

  // Tour Details
  'tour.price': {
    en: 'Contact for pricing',
    de: 'Preis auf Anfrage',
  },
  'tour.highlights': {
    en: 'Highlights:',
    de: 'Höhepunkte:',
  },
  'tour.bookNow': {
    en: 'Book Now',
    de: 'Jetzt buchen',
  },
  'tour.sendEnquiry': {
    en: 'Send enquiry',
    de: 'Anfrage senden',
  },
  'tour.badge.popular': {
    en: 'Popular',
    de: 'Beliebt',
  },

  // Tour Info
  'tourinfo.groups.title': {
    en: 'Group Size',
    de: 'Gruppengröße',
  },
  'tourinfo.groups.desc': {
    en: 'Flexible groups from intimate to large (up to 50 people)',
    de: 'Flexible Gruppen von intim bis groß (bis zu 50 Personen)',
  },
  'tourinfo.languages.title': {
    en: 'Languages',
    de: 'Sprachen',
  },
  'tourinfo.languages.desc': {
    en: 'Tours available in German, English, and Czech',
    de: 'Touren verfügbar auf Deutsch, Englisch und Tschechisch',
  },
  'tourinfo.booking.title': {
    en: 'Booking',
    de: 'Buchung',
  },
  'tourinfo.booking.desc': {
    en: 'Book at least 1 month in advance, last-minute possible',
    de: 'Mindestens 1 Monat im Voraus buchen, Last-Minute möglich',
  },
  'tourinfo.weather.title': {
    en: 'Weather',
    de: 'Wetter',
  },
  'tourinfo.weather.desc': {
    en: 'Tours run year-round! Dress appropriately',
    de: 'Touren das ganze Jahr über! Angemessen kleiden',
  },

  // Tours FAQ
  'tours.faq.title': {
    en: 'Frequently Asked Questions',
    de: 'Häufig gestellte Fragen',
  },
  'tours.faq.q1': {
    en: 'What languages do you offer tours in?',
    de: 'In welchen Sprachen bieten Sie Touren an?',
  },
  'tours.faq.a1': {
    en: 'I offer tours in German, English, and Czech. German is my primary touring language and most popular with visitors from Germany, Austria, and Switzerland.',
    de: 'Ich biete Touren auf Deutsch, Englisch und Tschechisch an. Deutsch ist meine Hauptsprache für Führungen und bei Besuchern aus Deutschland, Österreich und der Schweiz am beliebtesten.',
  },
  'tours.faq.q2': {
    en: 'How long do the tours last?',
    de: 'Wie lange dauern die Touren?',
  },
  'tours.faq.a2': {
    en: 'Most tours last between 3 and 4 hours. Custom tours can be shorter (2 hours) or longer (up to 6 hours) depending on your interests and schedule.',
    de: 'Die meisten Touren dauern zwischen 3 und 4 Stunden. Individuelle Touren können kürzer (2 Stunden) oder länger (bis zu 6 Stunden) sein, je nach Ihren Interessen und Zeitplan.',
  },
  'tours.faq.q3': {
    en: 'Can I book a private tour?',
    de: 'Kann ich eine private Tour buchen?',
  },
  'tours.faq.a3': {
    en: 'Yes! All my tours are private. I guide every tour personally, so you get my full attention. No large group bus tours. I work with individuals, couples, families, and groups of up to 50 people.',
    de: 'Ja! Alle meine Touren sind privat. Ich führe jede Tour persönlich, Sie haben meine volle Aufmerksamkeit. Keine großen Bustouren. Ich arbeite mit Einzelpersonen, Paaren, Familien und Gruppen von bis zu 50 Personen.',
  },
  'tours.faq.q4': {
    en: 'What is the Jewish Quarter certification?',
    de: 'Was ist die Zertifizierung für das Jüdische Viertel?',
  },
  'tours.faq.a4': {
    en: 'I hold a special accreditation from the Jewish Museum in Prague, which means I am authorized to provide in-depth guided tours of the Jewish Quarter, including the synagogues, the Old Jewish Cemetery, and the museum collections. Not all guides have this certification.',
    de: 'Ich besitze eine spezielle Akkreditierung des Jüdischen Museums in Prag, die mich berechtigt, vertiefte Führungen durch das Jüdische Viertel anzubieten, einschließlich der Synagogen, des Alten Jüdischen Friedhofs und der Museumssammlungen. Nicht alle Guides haben diese Zertifizierung.',
  },
  'tours.faq.q5': {
    en: 'How far in advance should I book?',
    de: 'Wie weit im Voraus sollte ich buchen?',
  },
  'tours.faq.a5': {
    en: 'I recommend booking at least 2 to 4 weeks in advance, especially during peak season (April through October). Last-minute bookings are sometimes possible if I have availability.',
    de: 'Ich empfehle, mindestens 2 bis 4 Wochen im Voraus zu buchen, besonders in der Hauptsaison (April bis Oktober). Kurzfristige Buchungen sind manchmal möglich, wenn ich verfügbar bin.',
  },

  // Contact FAQ
  'contact.faq.title': {
    en: 'Frequently Asked Questions',
    de: 'Häufig gestellte Fragen',
  },
  'contact.faq.q1': {
    en: 'How do I book a tour?',
    de: 'Wie buche ich eine Tour?',
  },
  'contact.faq.a1': {
    en: 'You can book by filling out the contact form on this page, sending me an email at zuzanamanova@email.cz, calling +420 721 231 933, or messaging me on WhatsApp. I respond within 24 hours.',
    de: 'Sie können buchen, indem Sie das Kontaktformular auf dieser Seite ausfüllen, mir eine E-Mail an zuzanamanova@email.cz senden, mich unter +420 721 231 933 anrufen oder mir auf WhatsApp schreiben. Ich antworte innerhalb von 24 Stunden.',
  },
  'contact.faq.q2': {
    en: 'What is the cancellation policy?',
    de: 'Wie ist die Stornierungspolitik?',
  },
  'contact.faq.a2': {
    en: 'Free cancellation up to 48 hours before the tour. For cancellations within 48 hours, please contact me directly and we can usually find a solution, such as rescheduling.',
    de: 'Kostenlose Stornierung bis zu 48 Stunden vor der Tour. Bei Stornierungen innerhalb von 48 Stunden kontaktieren Sie mich bitte direkt, und wir finden in der Regel eine Lösung, wie z.B. eine Terminverschiebung.',
  },
  'contact.faq.q3': {
    en: 'What should I wear and bring?',
    de: 'Was sollte ich anziehen und mitbringen?',
  },
  'contact.faq.a3': {
    en: 'Wear comfortable walking shoes. Prague has cobblestones, so flat shoes work best. In summer, bring water and sunscreen. In winter, dress warmly in layers. I provide all historical context and stories, so no guidebook needed!',
    de: 'Tragen Sie bequeme Wanderschuhe. Prag hat Kopfsteinpflaster, flache Schuhe sind am besten. Im Sommer bringen Sie Wasser und Sonnencreme mit. Im Winter kleiden Sie sich warm in Schichten. Ich liefere den gesamten historischen Kontext, kein Reiseführer nötig!',
  },

  // Contact Page
  'contact.header.title': {
    en: 'Contact Me',
    de: 'Kontaktieren Sie mich',
  },
  'contact.header.subtitle': {
    en: 'Ready to explore Prague? Let\'s plan your perfect tour!',
    de: 'Bereit, Prag zu erkunden? Lassen Sie uns Ihre perfekte Tour planen!',
  },
  'contact.intro.title': {
    en: 'Get in Touch',
    de: 'Kontakt aufnehmen',
  },
  'contact.intro.text': {
    en: 'I\'d love to hear from you! Whether you have questions about tours, want to book a visit, or need recommendations, feel free to reach out.',
    de: 'Ich würde gerne von Ihnen hören! Ob Sie Fragen zu Touren haben, einen Besuch buchen möchten oder Empfehlungen benötigen, zögern Sie nicht, mich zu kontaktieren.',
  },
  'contact.phone.title': {
    en: 'Phone / WhatsApp',
    de: 'Telefon / WhatsApp',
  },
  'contact.phone.whatsapp': {
    en: 'Chat on WhatsApp',
    de: 'WhatsApp-Chat',
  },
  'contact.email.title': {
    en: 'Email',
    de: 'E-Mail',
  },
  'contact.location.title': {
    en: 'Location',
    de: 'Standort',
  },
  'contact.location.text': {
    en: 'Prague, Czech Republic',
    de: 'Prag, Tschechische Republik',
  },
  'contact.response.title': {
    en: 'Response Time',
    de: 'Antwortzeit',
  },
  'contact.response.text': {
    en: 'Usually same day, within a few hours',
    de: 'In der Regel am selben Tag, innerhalb weniger Stunden',
  },

  // Contact Page (Booking Variant)
  'contact.booking.header.title': {
    en: 'Book a Tour',
    de: 'Tour buchen',
  },
  'contact.booking.header.subtitle': {
    en: 'Send a booking request via the form or message me on WhatsApp for the fastest reply.',
    de: 'Senden Sie eine Buchungsanfrage über das Formular oder schreiben Sie mir per WhatsApp für die schnellste Antwort.',
  },
  'contact.booking.header.eyebrow': {
    en: 'Your personal Prague tour',
    de: 'Ihre persönliche Prag-Tour',
  },
  'contact.booking.header.titleEnquiry': {
    en: 'Send an enquiry',
    de: 'Anfrage senden',
  },
  'contact.booking.header.subtitleEnquiry': {
    en: 'Tell me what you have in mind — I reply personally, usually within 24 hours.',
    de: 'Teilen Sie mir Ihre Wünsche mit — ich melde mich persönlich innerhalb von 24 Stunden.',
  },
  'contact.trust.rating': {
    en: '4.9 ★ · 14 reviews on TripAdvisor',
    de: '4,9 ★ · 14 Bewertungen auf TripAdvisor',
  },
  'contact.booking.intro.title': {
    en: 'Ready to book?',
    de: 'Bereit zur Buchung?',
  },
  'contact.booking.intro.text': {
    en: "Please share your preferred date, group size, language (DE/CZ), and interests. I'll confirm availability and tailor the tour for you.",
    de: 'Bitte teilen Sie mir Ihr Wunschdatum, die Gruppengröße, die Sprache (DE/CZ) und Ihre Interessen mit. Ich bestätige die Verfügbarkeit und passe die Tour für Sie an.',
  },
  'contact.booking.phone.whatsapp': {
    en: 'Book via WhatsApp',
    de: 'Per WhatsApp buchen',
  },

  // Jewish Quarter credential callout (booking page)
  'contact.jewish.credential.title': {
    en: 'Certified Jewish Quarter Guide',
    de: 'Zertifizierte Führerin für das Jüdische Viertel',
  },
  'contact.jewish.credential.text': {
    en: 'Zuzana holds official certification from the Jewish Museum in Prague — the institution that administers the historic synagogues, the Old Jewish Cemetery, and the Quarter\'s memorial sites. She knows the history behind every stone, every name, and every story that the Quarter still carries.',
    de: 'Zuzana besitzt die offizielle Zertifizierung des Jüdischen Museums in Prag — der Institution, die die historischen Synagogen, den Alten Jüdischen Friedhof und die Gedenkstätten des Viertels verwaltet. Sie kennt die Geschichte hinter jedem Stein, jedem Namen und jeder Geschichte, die das Viertel noch heute bewahrt.',
  },

  // Contact Form
  'form.title': {
    en: 'Send a Message',
    de: 'Nachricht senden',
  },
  'form.selectedTour': {
    en: 'Selected Tour',
    de: 'Ausgewählte Tour',
  },
  'form.name': {
    en: 'Your Name',
    de: 'Ihr Name',
  },
  'form.email': {
    en: 'Email',
    de: 'E-Mail',
  },
  'form.phone': {
    en: 'Phone Number',
    de: 'Telefonnummer',
  },
  'form.date': {
    en: 'Preferred Tour Date',
    de: 'Bevorzugtes Tourdatum',
  },
  'form.message': {
    en: 'Message',
    de: 'Nachricht',
  },
  'form.messagePlaceholder': {
    en: 'Tell me about your interests, group size, and what you\'d like to see in Prague...',
    de: 'Erzählen Sie mir von Ihren Interessen, Gruppengröße und was Sie in Prag sehen möchten...',
  },
  'form.submit': {
    en: 'Send Message',
    de: 'Nachricht senden',
  },

  // Contact Form (Booking Variant)
  'form.booking.title': {
    en: 'Booking Request',
    de: 'Buchungsanfrage',
  },
  'form.booking.message': {
    en: 'Your Request',
    de: 'Ihre Anfrage',
  },
  'form.booking.messagePlaceholder': {
    en: "E.g. 2 adults, April 12–13, language DE, prefer Old Town & Jewish Quarter + hidden gems. Hotel pick-up at 10:00 is great.",
    de: 'Z. B. 2 Erwachsene, 12.–13. April, Sprache DE, bevorzugt Altstadt & Jüdisches Viertel + versteckte Orte. Abholung im Hotel um 10:00 wäre ideal.',
  },
  'form.booking.submit': {
    en: 'Request Booking',
    de: 'Buchungsanfrage senden',
  },
  'form.groupSize': {
    en: 'Group Size',
    de: 'Gruppengröße',
  },
  'form.groupSize.placeholder': {
    en: 'Select...',
    de: 'Auswählen...',
  },
  'form.groupSize.1': { en: '1 person', de: '1 Person' },
  'form.groupSize.2': { en: '2 people', de: '2 Personen' },
  'form.groupSize.3': { en: '3 people', de: '3 Personen' },
  'form.groupSize.4': { en: '4 people', de: '4 Personen' },
  'form.groupSize.5': { en: '5 people', de: '5 Personen' },
  'form.groupSize.6plus': { en: '6+ people', de: '6+ Personen' },
  'form.tourInterest': {
    en: 'Tour Interest',
    de: 'Tour-Interesse',
  },
  'form.tourInterest.placeholder': {
    en: 'Select a tour...',
    de: 'Tour auswählen...',
  },
  'form.tourInterest.notSure': {
    en: 'Not sure yet / Custom',
    de: 'Noch unsicher / Individuell',
  },
  'form.phone.optional': {
    en: 'Phone (optional)',
    de: 'Telefon (optional)',
  },
  'form.booking.messagePlaceholderShort': {
    en: 'Anything else I should know? Special interests, accessibility needs, hotel pick-up...',
    de: 'Gibt es sonst noch etwas? Besondere Interessen, Barrierefreiheit, Hotelabholung...',
  },
  'form.booking.messageHint': {
    en: 'Helpful to include:',
    de: 'Hilfreiche Angaben:',
  },
  'form.booking.messageHintItems': {
    en: 'Number of people · preferred language (DE/EN/CZ) · interests · preferred pickup location',
    de: 'Anzahl Personen · bevorzugte Sprache (DE/EN/CZ) · Interessen · gewünschter Abholort',
  },
  'contact.booking.footer.prefer': {
    en: 'Prefer to message directly?',
    de: 'Lieber direkt schreiben?',
  },
  'contact.trustBadge': {
    en: 'Trusted by 300+ travelers from around the world',
    de: 'Über 300 zufriedene Reisende aus aller Welt',
  },
  'contact.review.quote': {
    en: '"Zuzana brought Prague to life for us. Her knowledge and passion made this the highlight of our trip. Absolutely book with her!"',
    de: '„Zuzana hat Prag für uns lebendig gemacht. Ihr Wissen und ihre Leidenschaft machten dies zum Höhepunkt unserer Reise. Unbedingt buchen!"',
  },
  'contact.review.author': {
    en: 'TripAdvisor review',
    de: 'TripAdvisor-Bewertung',
  },
  'contact.whatsapp.sticky': {
    en: 'Book via WhatsApp',
    de: 'Per WhatsApp buchen',
  },

  // Footer
  'footer.tagline': {
    en: 'Experience Prague through the eyes of a certified local expert',
    de: 'Erleben Sie Prag mit Ihrer deutschsprachigen Expertin und Spezialistin für Prag-Führungen',
  },
  'footer.cta.eyebrow': {
    en: 'Certified expert · 40 years experience',
    de: 'Zertifizierte Expertin · 40 Jahre Erfahrung',
  },
  'footer.cta.title': {
    en: 'Ready to discover Prague?',
    de: 'Bereit, Prag zu entdecken?',
  },
  'footer.certified': {
    en: 'Certified Prague expert guide',
    de: 'Zertifizierte Prag-Expertin & Spezialistin',
  },
  'footer.reviewCount': {
    en: '14 reviews',
    de: '14 Bewertungen',
  },
  'footer.tripadvisor.trustLine': {
    en: '4.9 ★ TripAdvisor',
    de: '4,9 ★ TripAdvisor',
  },
  'footer.tourhq.badge': {
    en: 'TourHQ verified',
    de: 'TourHQ verifiziert',
  },
  'footer.tourhq.sub': {
    en: 'Profile & reviews',
    de: 'Profil & Bewertungen',
  },
  'footer.contact': {
    en: 'Contact',
    de: 'Kontakt',
  },
  'footer.quicklinks': {
    en: 'Quick Links',
    de: 'Schnelllinks',
  },
  
  // Reviews / Social Proof
  'reviews.tripadvisor': {
    en: 'Rated 4.9/5 on TripAdvisor',
    de: 'Bewertet mit 4,9/5 bei TripAdvisor',
  },
  'footer.follow': {
    en: 'Follow Us',
    de: 'Folgen Sie uns',
  },
  'footer.rights': {
    en: 'All rights reserved',
    de: 'Alle Rechte vorbehalten',
  },
  'footer.privacy': {
    en: 'Privacy Policy',
    de: 'Datenschutz',
  },
  'footer.terms': {
    en: 'Terms & Conditions',
    de: 'AGB',
  },

  // Blog Page
  'blog.title': {
    en: 'Prague Travel Blog',
    de: 'Prag Reiseblog',
  },
  'blog.subtitle': {
    en: 'Tips, stories, and insights about exploring Prague',
    de: 'Tipps, Geschichten und Einblicke über die Erkundung Prags',
  },
  'blog.cta.defaultTitle': {
    en: 'Ready to explore Prague?',
    de: 'Bereit, Prag zu erkunden?',
  },
  'blog.cta.defaultBody': {
    en: 'Discover Prague with Ing. Zuzana Manová — personal, certified, unforgettable.',
    de: 'Entdecken Sie Prag mit Ing. Zuzana Manová — persönlich, zertifiziert, unvergesslich.',
  },
  'blog.cta.askQuestion': {
    en: 'Ask a question',
    de: 'Frage stellen',
  },
  'blog.readMore': {
    en: 'Read More',
    de: 'Weiterlesen',
  },
  'blog.backToBlog': {
    en: 'Back to Blog',
    de: 'Zurück zum Blog',
  },
  'blog.share': {
    en: 'Share this post',
    de: 'Diesen Beitrag teilen',
  },
  'blog.related': {
    en: 'More Prague insights',
    de: 'Mehr Prag-Impulse',
  },
  'blog.aboutBox.title': {
    en: 'About Zuza Prague Tours',
    de: 'Über Zuza Prague Tours',
  },
  'blog.tocTitle': {
    en: 'Table of contents',
    de: 'Inhalt',
  },
  'blog.featuredTour.title': {
    en: 'Featured tour: Prague highlights',
    de: 'Empfohlene Tour: Prag‑Highlights',
  },
  'blog.featuredTour.desc': {
    en: 'Private walking tours tailored to your pace — Old Town, Castle, hidden corners.',
    de: 'Private Stadtführungen in Ihrem Tempo — Altstadt, Burg, versteckte Winkel.',
  },
  'blog.featuredTour.cta': {
    en: 'Book now',
    de: 'Jetzt buchen',
  },
  'blog.newsletter.title': {
    en: 'Stay in touch',
    de: 'Bleiben Sie in Kontakt',
  },
  'blog.newsletter.blurb': {
    en: 'Ask a question or tell us when you are visiting — we will get back to you.',
    de: 'Stellen Sie eine Frage oder nennen Sie Ihr Reisedatum — wir melden uns.',
  },
  'blog.newsletter.cta': {
    en: 'Contact us',
    de: 'Kontakt aufnehmen',
  },
  'blog.shareMore': {
    en: 'Share',
    de: 'Teilen',
  },

  // Blog Posts
  'blog.post1.title': {
    en: 'Best Time to Visit Prague: A Seasonal Guide',
    de: 'Beste Reisezeit für Prag: Ein Saisonführer',
  },
  'blog.post1.excerpt': {
    en: 'Discover the perfect season to explore Prague based on your preferences. From blooming spring gardens to magical winter markets...',
    de: 'Entdecken Sie die perfekte Jahreszeit, um Prag zu erkunden. Von blühenden Frühlingsgärten bis zu magischen Wintermärkten...',
  },
  'blog.post1.date': {
    en: 'January 15, 2024',
    de: '15. Januar 2024',
  },
  'blog.post1.content': {
    en: `<h2>When Is the Best Time to Visit Prague?</h2>
<p>Prague is beautiful year-round, but the <strong>best time to visit</strong> depends on your interests: blooming <em>spring gardens</em>, lively <em>summer evenings</em>, colorful <em>autumn foliage</em>, or romantic <em>winter markets</em>. As a certified local guide, I help you plan the perfect season and itinerary—no crowds, maximum charm.</p>
<h3>Spring (March–May)</h3>
<ul>
  <li>Cherry blossoms in Petřín and quiet mornings on <a href="/tours">Charles Bridge</a></li>
  <li>Mild weather for <strong>Old Town & Jewish Quarter</strong> walks</li>
  <li>Fewer crowds than summer</li>
  </ul>
<h3>Summer (June–August)</h3>
<ul>
  <li>Long golden evenings and open-air concerts</li>
  <li>Perfect for <a href="/tours">Prague Castle</a> courtyards and river views</li>
  <li>Book in advance—peak season</li>
</ul>
<h3>Autumn (September–November)</h3>
<ul>
  <li>Warm colors in royal gardens and vineyards of Letná</li>
  <li>Still pleasant temperatures and thinner crowds</li>
</ul>
<h3>Winter (December–February)</h3>
<ul>
  <li>Magical <strong>Christmas markets</strong> and snowy rooftops</li>
  <li>Great for museums and cozy cafés</li>
</ul>
<p class="lead">Want a seasonal, private route tailored to you? <a href="/contact#contact-title">Contact me</a> and I’ll plan your <strong>perfect Prague tour</strong>.</p>`,
    de: `<h2>Wann ist die beste Reisezeit für Prag?</h2>
<p>Prag ist das ganze Jahr über wunderschön. Die <strong>beste Reisezeit</strong> hängt davon ab, was Sie suchen: blühende <em>Frühlingsgärten</em>, lebendige <em>Sommerabende</em>, farbenfroher <em>Herbst</em> oder romantische <em>Wintermärkte</em>. Als zertifizierte lokale Führerin plane ich mit Ihnen die ideale Saison—ohne Menschenmengen, mit maximalem Charme.</p>
<h3>Frühling (März–Mai)</h3>
<ul>
  <li>Kirschblüten auf dem Petřín und ruhige Morgen auf der <a href="/tours">Karlsbrücke</a></li>
  <li>Angenehm für die <strong>Altstadt & das Jüdische Viertel</strong></li>
  <li>Weniger Besucher als im Sommer</li>
</ul>
<h3>Sommer (Juni–August)</h3>
<ul>
  <li>Lange goldene Abende und Open-Air-Konzerte</li>
  <li>Ideal für <a href="/tours">Burgareale</a> und Flussblicke</li>
  <li>Frühzeitig buchen—Hauptsaison</li>
</ul>
<h3>Herbst (September–November)</h3>
<ul>
  <li>Warme Farben in königlichen Gärten und den Weinbergen von Letná</li>
  <li>Angenehme Temperaturen und dünnere Besucherzahlen</li>
</ul>
<h3>Winter (Dezember–Februar)</h3>
<ul>
  <li>Zauberhafte <strong>Weihnachtsmärkte</strong> und verschneite Dächer</li>
  <li>Perfekt für Museen und gemütliche Cafés</li>
</ul>
<p class="lead">Wünschen Sie eine saisonale, private Route? <a href="/contact#contact-title">Kontaktieren Sie mich</a>—ich plane Ihre <strong>perfekte Prag-Tour</strong>.</p>`
  },

  'blog.post2.title': {
    en: 'Hidden Gems in Prague: Secret Courtyards, Cafés, and Views',
    de: 'Prag Geheimtipps: Versteckte Höfe, Cafés und Aussichten',
  },
  'blog.post2.excerpt': {
    en: 'Local picks for lesser-known Prague: hidden courtyards, baroque gardens, vintage cafés, and photo viewpoints you can actually find.',
    de: 'Lokale Auswahl für unbekanntes Prag: versteckte Innenhöfe, barocke Gärten, Vintage-Cafés und Fotospots, die man wirklich findet.',
  },
  'blog.post2.date': {
    en: 'January 8, 2024',
    de: '8. Januar 2024',
  },
  'blog.post2.content': {
    en: `<h2>Hidden Prague You Can Actually Visit</h2>
<p>Here’s a realistic, walkable set of <strong>quiet courtyards, baroque gardens, heritage cafés</strong> and viewpoints—perfect to combine into a 2–3 hour loop in the Old Town and Lesser Town.</p>
<h3>Courtyards & Passages (Old Town)</h3>
<ul>
  <li><strong>Ungelt (Týn Courtyard)</strong>: medieval trade court tucked behind Týn Church.</li>
  <li><strong>Clam-Gallas Palace</strong>: baroque courtyard; check for seasonal access.</li>
  <li><strong>Franciscan Garden</strong>: a calm green pocket near Wenceslas Square.</li>
  <li><strong>Passage to Platýz</strong>: vintage décor and quiet arcades.</li>
  </ul>
<h3>Gems in Lesser Town (Malá Strana)</h3>
<ul>
  <li><strong>Vrtba Garden</strong> (seasonal): terraced baroque garden with top views.</li>
  <li><strong>Wallenstein Garden</strong>: peacocks, dripstone wall, Senate arcades.</li>
  <li><strong>Kampa backstreets</strong>: mill wheels, hidden riverside nooks.</li>
  </ul>
<h3>Classic Cafés with Character</h3>
<ul>
  <li><strong>Café Louvre</strong> (1902): literary haunt; billiard room, pastries.</li>
  <li><strong>Café Slavia</strong>: National Theatre vista; art-deco vibe.</li>
  <li><strong>New Scene (Nová scéna) café</strong>: glass façade, modernist feel.</li>
  </ul>
<h3>Loop Itinerary Idea</h3>
<p>Start at Old Town Square → Ungelt → Clam-Gallas → Charles Bridge (side views) → Vrtba/Wallenstein → Kampa → finish at National Theatre with a coffee. <em>Want a curated route?</em> <a href="/book?tour=Hidden%20Prague#contact-title">Book a private Hidden Prague tour</a>.</p>`,
    de: `<h2>Verstecktes Prag – wirklich begehbar</h2>
<p>Hier eine realistische, gut gehbare Auswahl an <strong>stillen Innenhöfen, barocken Gärten, Traditions-Cafés</strong> und Aussichtspunkten—ideal als 2–3‑Stunden-Runde durch Altstadt und Kleinseite.</p>
<h3>Höfe & Passagen (Altstadt)</h3>
<ul>
  <li><strong>Ungelt (Týn-Hof)</strong>: mittelalterlicher Handelshof hinter der Teynkirche.</li>
  <li><strong>Palais Clam-Gallas</strong>: barocker Innenhof; saisonale Zugänge prüfen.</li>
  <li><strong>Franziskanergarten</strong>: grüne Ruheoase nahe Wenzelsplatz.</li>
  <li><strong>Passage zum Platýz</strong>: Vintage-Details und stille Arkaden.</li>
  </ul>
<h3>Schätze in der Kleinseite (Malá Strana)</h3>
<ul>
  <li><strong>Vrtba-Garten</strong> (saisonal): terrassierter Barockgarten mit Top-Blicken.</li>
  <li><strong>Wallenstein-Garten</strong>: Pfauen, Tropfsteinwand, Arkaden am Senat.</li>
  <li><strong>Kampa-Hintergassen</strong>: Mühlräder, ruhige Plätze am Wasser.</li>
  </ul>
<h3>Traditions-Cafés mit Charakter</h3>
<ul>
  <li><strong>Café Louvre</strong> (1902): Literaten; Billardzimmer, Patisserie.</li>
  <li><strong>Café Slavia</strong>: Blick aufs Nationaltheater; Art-Déco-Atmosphäre.</li>
  <li><strong>Café der Neuen Szene</strong>: Glasfassade, modernistisches Flair.</li>
  </ul>
<h3>Routenidee</h3>
<p>Start am Altstädter Ring → Ungelt → Clam-Gallas → Karlsbrücke (Seitenblicke) → Vrtba/Wallenstein → Kampa → Abschluss am Nationaltheater mit Kaffee. <em>Wunschroute?</em> <a href="/book?tour=Verstecktes%20Prag#contact-title">Private Tour buchen</a>.</p>`
  },

  // New Post 12: Franz Kafka in Prague
  'blog.post12.title': {
    en: 'Franz Kafka in Prague: All Places to Visit + Walking Itinerary',
    de: 'Franz Kafka in Prag: Alle Orte – Rundgang & Reiseroute',
  },
  'blog.post12.excerpt': {
    en: 'The definitive Kafka-in-Prague route: birthplace, plaques, cafés, museum, Golden Lane and more—timed, map-friendly, and doable in half a day.',
    de: 'Die vollständige Kafka-Route in Prag: Geburtshaus, Tafeln, Cafés, Museum, Goldenes Gässchen u. mehr—mit Zeiten, Karte und in einem halben Tag machbar.',
  },
  'blog.post12.date': {
    en: 'October 19, 2025',
    de: '19. Oktober 2025',
  },
  'blog.post12.content': {
    en: `<h2>Kafka in Prague: All Places to Visit (With Route)</h2>
<p class="lead"><strong>Franz Kafka (1883–1924)</strong> wrote in a Prague shaped by <em>German–Czech–Jewish</em> culture. Use this map-friendly <strong>itinerary</strong> to see every essential Kafka place in half a day.</p>
<h3>Stop 1 — Birthplace by Týn Church (Old Town)</h3>
<p>The original building is gone, but the <strong>site by Týn Church</strong> anchors Kafka’s early years steps from Old Town Square.</p>
<h3>Stop 2 — Old Town Plaques & Charles University</h3>
<p>Find <strong>plaques and addresses</strong> where Kafka lived or studied around <strong>Charles University</strong> and the Old Town grid.</p>
<h3>Stop 3 — Kafka Statue & Jewish Quarter Context</h3>
<p>Visit the <strong>Kafka statue</strong> near the Spanish Synagogue and add the <strong>Jewish Museum sites</strong> for the period context.</p>
<div class="blog-inline-image"><img src="/images/blog-kafka-2.jpg" alt="Franz Kafka — Rotating Head by David Černý near Národní třída" loading="lazy" /><div class="image-credit">Franz Kafka – Rotating Head by David Černý (near Národní třída / Quadrio)</div></div>
<h3>Optional — Franz Kafka “Rotating Head” (David Černý)</h3>
<p>The kinetic sculpture by <strong>David Černý</strong>—often called the <em>Rotating Head</em>—stands by <strong>Quadrio</strong> close to <strong>Národní třída</strong>. It’s an easy add-on between the Old Town and the café stops.</p>
<h3>Stop 4 — Kafka’s Golden Lane House (Prague Castle)</h3>
<p>A small house in <strong>Golden Lane</strong> served as a writing retreat—combine with a <a href="/tours">Prague Castle walk</a> for top views.</p>
<h3>Stop 5 — Cafés: Louvre & Slavia</h3>
<p>Classic cafés on Národní—<strong>Café Louvre</strong> and <strong>Café Slavia</strong>—tie into Prague’s literary circles. Perfect rest stops.</p>
<h3>Stop 6 — Franz Kafka Museum (Lesser Town)</h3>
<p>On the river, the <strong>Franz Kafka Museum</strong> is an hour of manuscripts, photos and multimedia—ideal at the end of the loop.</p>
<div class="did-you-know"><h4>Did you know?</h4><p>Kafka’s <em>day job</em> at the <strong>Workers’ Accident Insurance Institute</strong> helped shape his themes of bureaucracy and modernity.</p></div>
<h3>Timing & Route (3–4 hours)</h3>
<ul>
  <li>Old Town core (Birthplace, plaques): 45–60 min</li>
  <li>Jewish Quarter + statue: 30–45 min</li>
  <li>Optional: Rotating Head (Quadrio): 10–15 min</li>
  <li>Castle Golden Lane add-on: 45–60 min (ticketed)</li>
  <li>Cafés (Louvre/Slavia): 20–30 min break</li>
  <li>Kafka Museum: 45–60 min</li>
  <li>Walking links between stops: ~30–45 min total</li>
  </ul>
<div class="blog-cta-box">
  <h3>Open the Kafka Route in Google Maps</h3>
  <p>Birthplace (Týn) → Old Town plaques → Jewish Quarter statue → Rotating Head (Quadrio) → Golden Lane → Cafés (Louvre/Slavia) → Kafka Museum</p>
  <div class="cta-buttons">
    <a class="btn btn-primary" target="_blank" rel="noopener" href="https://www.google.com/maps/dir/?api=1&origin=Old+Town+Square,+Prague&destination=Franz+Kafka+Museum,+Ciheln%C3%A1+2b,+110+00+Praha+1&travelmode=walking&waypoints=T%C3%BDnsk%C3%BD+chram|Charles+University,+Prague|Statue+of+Franz+Kafka,+Du%C5%A1n%C3%A1|Head+of+Franz+Kafka,+Prague|Golden+Lane,+Prague+Castle|Caf%C3%A9+Louvre,+N%C3%A1rodn%C3%AD+22|Caf%C3%A9+Slavia,+Smetanovo+n%C3%A1b%C5%99e%C5%BE%C3%AD+1">Open in Google Maps</a>
    <a class="btn btn-outline" href="/book?tour=Kafka%20Itinerary#contact-title">Book a guided Kafka tour</a>
  </div>
</div>
<div class="blog-cta-box">
  <h3>Want a calm, guide-led Kafka route?</h3>
  <p>With the Rotating Head, hidden Old Town plaques and museum timing—paced to <strong>your</strong> tempo. <a href="/book?tour=Kafka%20Itinerary#contact-title">Book a private tour</a> and I’ll tailor the route, timings and tickets.</p>
</div>
<p>Do it point-to-point on foot or book a <a href="/book?tour=Kafka%20Itinerary#contact-title">Kafka private tour</a> for navigation, tickets and context.</p>`,
    de: `<h2>Kafka in Prag: Alle Orte (mit Route)</h2>
<p class="lead"><strong>Franz Kafka (1883–1924)</strong> schrieb in einem Prag zwischen <em>deutscher, tschechischer und jüdischer</em> Kultur. Mit dieser <strong>Itinerar</strong> sehen Sie alle wichtigen Kafka-Orte in einem halben Tag.</p>
<h3>Stopp 1 — Geburtshaus bei der Teynkirche (Altstadt)</h3>
<p>Das ursprüngliche Haus existiert nicht mehr, doch der <strong>Ort an der Teynkirche</strong> markiert Kafkas frühe Jahre, nur Schritte vom Altstädter Ring.</p>
<h3>Stopp 2 — Tafeln & Karlsuniversität</h3>
<p>Finden Sie <strong>Gedenktafeln und Adressen</strong>, an denen Kafka lebte oder studierte—rund um die <strong>Karlsuniversität</strong> und das Altstadtgitter.</p>
<h3>Stopp 3 — Kafka-Statue & Kontext Jüdisches Viertel</h3>
<p>Besuchen Sie die <strong>Kafka-Statue</strong> nahe der Spanischen Synagoge und fügen Sie die <strong>Stätten des Jüdischen Museums</strong> für den Zeitkontext hinzu.</p>
<div class="blog-inline-image"><img src="/images/blog-kafka-2.jpg" alt="Franz Kafka – Rotierender Kopf von David Černý nahe Národní třída" loading="lazy" /><div class="image-credit">Franz Kafka – Rotierender Kopf von David Černý (nahe Národní třída / Quadrio)</div></div>
<h3>Optional — „Rotierender Kopf“ (David Černý)</h3>
<p>Die kinetische Skulptur von <strong>David Černý</strong>—der <em>Rotierende Kopf</em>—steht am <strong>Quadrio</strong> nahe der <strong>Národní třída</strong>. Leicht zwischen Altstadt und Café‑Stopps einzubauen.</p>
<h3>Stopp 4 — Kafkas Häuschen im Goldenen Gässchen (Burg)</h3>
<p>Ein kleines Haus im <strong>Goldenen Gässchen</strong> diente als Schreibrefugium—ideal kombinierbar mit einem <a href="/tours">Burgspaziergang</a>.</p>
<h3>Stopp 5 — Cafés: Louvre & Slavia</h3>
<p>Klassische Cafés an der Národní—<strong>Café Louvre</strong> und <strong>Café Slavia</strong>—knüpfen an Prags literarische Kreise an. Perfekte Pausenstopps.</p>
<h3>Stopp 6 — Franz-Kafka-Museum (Kleinseite)</h3>
<p>Am Fluss bietet das <strong>Kafka-Museum</strong> eine Stunde Handschriften, Fotos und Medien—ideal zum Abschluss des Rundgangs.</p>
<div class="did-you-know"><h4>Wussten Sie schon?</h4><p>Kafkas <em>Tagesjob</em> bei der <strong>Arbeiter-Unfallversicherungsanstalt</strong> prägte seine Themen von Bürokratie und Moderne.</p></div>
<h3>Zeitplanung & Route (3–4 Stunden)</h3>
<ul>
  <li>Altstadtkern (Geburtshaus, Tafeln): 45–60 Min</li>
  <li>Jüdisches Viertel + Statue: 30–45 Min</li>
  <li>Optional: Rotierender Kopf (Quadrio): 10–15 Min</li>
  <li>Burg/Goldenes Gässchen optional: 45–60 Min (Ticket)</li>
  <li>Cafés (Louvre/Slavia): 20–30 Min Pause</li>
  <li>Kafka-Museum: 45–60 Min</li>
  <li>Wege zwischen Stopps: ca. 30–45 Min</li>
  </ul>
<div class="blog-cta-box">
  <h3>Kafka-Route in Google Maps öffnen</h3>
  <p>Geburtsort (Teyn) → Altstadt-Tafeln → Statue im Jüdischen Viertel → Rotierender Kopf (Quadrio) → Goldenes Gässchen → Cafés (Louvre/Slavia) → Kafka-Museum</p>
  <div class="cta-buttons">
    <a class="btn btn-primary" target="_blank" rel="noopener" href="https://www.google.com/maps/dir/?api=1&origin=Altst%C3%A4dter+Ring,+Prag&destination=Franz+Kafka+Museum,+Ciheln%C3%A1+2b,+110+00+Praha+1&travelmode=walking&waypoints=Teynkirche|Karlsuniversit%C3%A4t,+Prag|Kafka-Statue,+Du%C5%A1n%C3%A1|Head+of+Franz+Kafka,+Prague|Goldenes+G%C3%A4sschen,+Prager+Burg|Caf%C3%A9+Louvre,+N%C3%A1rodn%C3%AD+22|Caf%C3%A9+Slavia,+Smetanovo+n%C3%A1b%C5%99e%C5%BE%C3%AD+1">In Google Maps öffnen</a>
    <a class="btn btn-outline" href="/book?tour=Kafka%20Itinerary#contact-title">Geführte Kafka-Tour buchen</a>
  </div>
</div>
<div class="blog-cta-box">
  <h3>Möchten Sie eine ruhige Kafka-Route mit allen Highlights?</h3>
  <p>Mit Rotierendem Kopf, versteckten Altstadt-Tafeln und optimalen Museumszeiten—genau in <strong>Ihrem</strong> Tempo. <a href="/book?tour=Kafka%20Itinerary#contact-title">Private Tour buchen</a>—ich passe Route, Zeiten und Tickets an.</p>
</div>
<p>Machen Sie es zu Fuß oder buchen Sie eine <a href="/book?tour=Kafka%20Itinerary#contact-title">Kafka-Privatführung</a> mit Navigation, Tickets und Kontext.</p>`
  },

  'blog.post3.title': {
    en: 'Prague at Night: A Magical Experience',
    de: 'Prag bei Nacht: Ein magisches Erlebnis',
  },
  'blog.post3.excerpt': {
    en: 'Discover the enchanting beauty of Prague after dark. From illuminated bridges to romantic evening walks, experience the magic of nocturnal Prague...',
    de: 'Entdecken Sie die bezaubernde Schönheit Prags nach Einbruch der Dunkelheit. Von beleuchteten Brücken bis zu romantischen Abendspaziergängen...',
  },
  'blog.post3.date': {
    en: 'January 1, 2024',
    de: '1. Januar 2024',
  },
  'blog.post3.content': {
    en: `<h2>Why Prague Shines After Dark</h2>
<p>At night, Prague transforms—<strong>illuminated bridges</strong>, glowing church facades, and quiet cobblestones. My evening routes avoid crowds and highlight the most atmospheric viewpoints for photography and romance.</p>
<h3>Golden Hour to Blue Hour</h3>
<ul>
  <li>Charles Bridge silhouettes and Vltava reflections</li>
  <li>Prague Castle skyline from riverside spots</li>
</ul>
<h3>Evening Walk Highlights</h3>
<ul>
  <li>Lesser Town lanterns and hidden alleys</li>
  <li>Old Town’s architectural details under soft light</li>
</ul>
<p class="lead">Book a private <a href="/tours">night tour</a>—I’ll plan safe, stunning viewpoints and insider photo spots.</p>`,
    de: `<h2>Warum Prag nachts glänzt</h2>
<p>Nachts verwandelt sich Prag—<strong>beleuchtete Brücken</strong>, leuchtende Kirchenfassaden und stille Kopfsteinpflaster. Meine Abendrouten meiden Menschenmengen und zeigen die stimmungsvollsten Ausblicke für Fotos und Romantik.</p>
<h3>Goldene Stunde bis Blaue Stunde</h3>
<ul>
  <li>Silhouetten auf der Karlsbrücke und Vltava-Reflexe</li>
  <li>Schloss-Skyline von Flussufern</li>
</ul>
<h3>Highlights des Abendspaziergangs</h3>
<ul>
  <li>Laternen in der Kleinseite und versteckte Gassen</li>
  <li>Architekturdetails der Altstadt im sanften Licht</li>
</ul>
<p class="lead">Buchen Sie eine private <a href="/tours">Nacht-Tour</a>—mit sicheren, eindrucksvollen Aussichtspunkten und Insider-Fotospots.</p>`
  },

  'blog.post4.title': {
    en: 'The Jewish Quarter: History and Heritage',
    de: 'Das Jüdische Viertel: Geschichte und Erbe',
  },
  'blog.post4.excerpt': {
    en: 'Explore the rich history of Prague\'s Jewish Quarter, one of Europe\'s most significant Jewish heritage sites...',
    de: 'Erkunden Sie die reiche Geschichte des Prager Jüdischen Viertels, einer der bedeutendsten jüdischen Stätten Europas...',
  },
  'blog.post4.date': {
    en: 'December 20, 2023',
    de: '20. Dezember 2023',
  },
  'blog.post4.content': {
    en: `<h2>The Jewish Quarter: Memory and Heritage</h2>
<p>Prague’s <strong>Jewish Quarter (Josefov)</strong> is among Europe’s most significant Jewish heritage sites. With my <em>special certification</em>, I guide you through synagogues, the Old Jewish Cemetery, and the community’s powerful history with accuracy and respect.</p>
<h3>Highlights</h3>
<ul>
  <li>Pinkas Synagogue and the memorial inscriptions</li>
  <li>Old-New Synagogue—Europe’s oldest active synagogue</li>
  <li>Old Jewish Cemetery’s layered centuries</li>
</ul>
<p class="lead">For a thoughtful, historically grounded visit, <a href="/contact#contact-title">contact me</a>—I tailor tours to your interests and timeframe.</p>`,
    de: `<h2>Das Jüdische Viertel: Erinnerung und Erbe</h2>
<p>Prags <strong>Jüdisches Viertel (Josefov)</strong> zählt zu den bedeutendsten Stätten jüdischen Erbes in Europa. Mit meiner <em>Sonderzertifizierung</em> führe ich Sie durch Synagogen, den Alten Jüdischen Friedhof und die bewegende Geschichte der Gemeinde—präzise und respektvoll.</p>
<h3>Höhepunkte</h3>
<ul>
  <li>Pinkas-Synagoge und die Gedenkinschriften</li>
  <li>Altneu-Synagoge—älteste aktive Synagoge Europas</li>
  <li>Jahrhunderte im Alten Jüdischen Friedhof</li>
</ul>
<p class="lead">Für einen einfühlsamen, fundierten Besuch <a href="/contact#contact-title">kontaktieren Sie mich</a>—ich passe die Führung an Ihre Interessen und Ihre Zeit an.</p>`
  },

  // New Post 5: Top 21 Instagrammable Places
  'blog.post5.title': {
    en: 'Top 21 Instagrammable Places in Prague',
    de: 'Top 21 Instagram-taugliche Orte in Prag',
  },
  'blog.post5.excerpt': {
    en: 'From iconic bridges to hidden courtyards—discover Prague’s most photogenic locations and exact spots for the perfect shot.',
    de: 'Von ikonischen Brücken bis zu versteckten Höfen—entdecken Sie Prags fotogenste Orte und präzise Spots für das perfekte Foto.',
  },
  'blog.post5.date': {
    en: 'October 12, 2025',
    de: '12. Oktober 2025',
  },
  'blog.post5.content': {
    en: `<h2>Prague’s Most Photogenic Spots</h2>
<p>Here are <strong>21 tried-and-true locations</strong> I use on tours and photo walks. Arrive early, mind the light, and always respect local spaces.</p>
<ol>
  <li><h3>Charles Bridge (Statues & Towers)</h3><p>Best at dawn from the Old Town tower towards the Castle.</p></li>
  <li><h3>Prague Castle Viewpoints</h3><p>Lookouts by St. Wenceslas Vineyard and Hradčanské náměstí.</p></li>
  <li><h3>Old Town Square</h3><p>Astronomical Clock details and wide shots at sunrise.</p></li>
  <li><h3>Letná Park</h3><p>River S-curves and bridge panorama.</p></li>
  <li><h3>Vrtba Garden</h3><p>Baroque terraces and city roofs (seasonal).</p></li>
  <li><h3>Mala Strana Alleys</h3><p>Lantern-lit streets and pastel facades.</p></li>
  <li><h3>John Lennon Wall</h3><p>Colorful backdrop with constant change.</p></li>
  <li><h3>Petřín Lookout</h3><p>City skyline with trees and blossom in spring.</p></li>
  <li><h3>Rudolfinum & Riverbank</h3><p>Castle reflections at blue hour.</p></li>
  <li><h3>Municipal House</h3><p>Art Nouveau details and café interiors.</p></li>
  <li><h3>Wallenstein Garden</h3><p>Peacocks, arcades, and the dripstone wall.</p></li>
  <li><h3>Vyšehrad Fortifications</h3><p>Vltava vistas and neo-Gothic spires.</p></li>
  <li><h3>Powder Tower</h3><p>Gothic textures and dramatic perspective.</p></li>
  <li><h3>Clementinum Courtyard</h3><p>Elegant symmetry and stone colonnades.</p></li>
  <li><h3>Strahov Monastery</h3><p>Libraries (with permits) and sweeping views.</p></li>
  <li><h3>Kafka Head</h3><p>Moving mirrors—great for creative reflections.</p></li>
  <li><h3>Dancing House</h3><p>Modern contrast with classic riverfront.</p></li>
  <li><h3>Old Jewish Cemetery</h3><p>Respectful angles—no flash, no posing.</p></li>
  <li><h3>Charles Bridge from Kampa</h3><p>Romantic shots through willows.</p></li>
  <li><h3>St. Nicholas Church (Mala Strana)</h3><p>Domes and curves—inside and out.</p></li>
  <li><h3>Štefánik Bridge Lookout</h3><p>Multiple bridges in one frame at sunset.</p></li>
  </ol>
<p class="lead">Want me to map a sunrise-to-night photo route just for you? <a href="/contact#contact-title">Message me</a>—I’ll tailor a private tour.</p>`,
    de: `<h2>Prags fotogenste Orte</h2>
<p>Hier sind <strong>21 erprobte Spots</strong>, die ich auf Führungen und Fotowalks nutze. Früh ankommen, auf Licht achten und Orte respektieren.</p>
<ol>
  <li><h3>Karlsbrücke (Statuen & Türme)</h3><p>Am besten bei Sonnenaufgang vom Altstädter Turm Richtung Schloss.</p></li>
  <li><h3>Burg-Aussichtspunkte</h3><p>Blicke beim Wenzelsweinberg und auf dem Hradschin-Platz.</p></li>
  <li><h3>Altstädter Ring</h3><p>Astronomische Uhr im Detail, Weitwinkel bei Sonnenaufgang.</p></li>
  <li><h3>Letná-Park</h3><p>Flussschleifen und Brückenpanorama.</p></li>
  <li><h3>Vrtba-Garten</h3><p>Barockterrassen und Dächer (saisonal).</p></li>
  <li><h3>Gassen der Kleinseite</h3><p>Laternenlicht und Pastellfassaden.</p></li>
  <li><h3>John-Lennon-Mauer</h3><p>Bunte Kulisse im Wandel.</p></li>
  <li><h3>Petřín-Aussicht</h3><p>Stadtbild mit Bäumen, Blüte im Frühling.</p></li>
  <li><h3>Rudolfinum & Ufer</h3><p>Schlossreflexe zur Blauen Stunde.</p></li>
  <li><h3>Gemeindehaus</h3><p>Jugendstil-Details und Café-Interieurs.</p></li>
  <li><h3>Wallenstein-Garten</h3><p>Pfauen, Arkaden, Tropfsteinwand.</p></li>
  <li><h3>Vyšehrad</h3><p>Vltava-Blicke und neugotische Türme.</p></li>
  <li><h3>Pulverturm</h3><p>Gotische Texturen, dramatische Perspektive.</p></li>
  <li><h3>Klementinum-Hof</h3><p>Elegante Symmetrie und Säulengänge.</p></li>
  <li><h3>Strahov-Kloster</h3><p>Bibliotheken (mit Genehmigung) und Panorama.</p></li>
  <li><h3>Kafka-Kopf</h3><p>Spiegelungen in Bewegung.</p></li>
  <li><h3>Das Tanzende Haus</h3><p>Moderne im Kontrast zur klassischen Uferfront.</p></li>
  <li><h3>Alter Jüdischer Friedhof</h3><p>Respektvolle Winkel—kein Blitz, keine Posen.</p></li>
  <li><h3>Karlsbrücke von Kampa</h3><p>Romantische Bilder durch Weiden.</p></li>
  <li><h3>St.-Nikolaus (Kleinseite)</h3><p>Kuppeln und Kurven—innen wie außen.</p></li>
  <li><h3>Štefánik-Brücke</h3><p>Mehrere Brücken im Bild bei Sonnenuntergang.</p></li>
  </ol>
<p class="lead">Möchten Sie eine maßgeschneiderte Foto-Route von Sonnenaufgang bis Nacht? <a href="/contact#contact-title">Schreiben Sie mir</a>—ich plane eine private Tour.</p>`
  },

  // New Post 6: Václav Havel in Prague
  'blog.post6.title': {
    en: 'Václav Havel in Prague: A Walking Tour of Freedom',
    de: 'Václav Havel in Prag: Ein Spaziergang der Freiheit',
  },
  'blog.post6.excerpt': {
    en: "Follow the footsteps of Václav Havel across Prague—Velvet Revolution sites, civic forums, theaters, and cafés that shaped a nation's path to freedom.",
    de: 'Folgen Sie den Spuren Václav Havels durch Prag—Schauplätze der Samtenen Revolution, Bürgerforen, Theater und Cafés, die den Weg zur Freiheit prägten.',
  },
  'blog.post6.date': {
    en: 'October 18, 2025',
    de: '18. Oktober 2025',
  },
  'blog.post6.content': {
    en: `<h2>Prague Through Havel’s Eyes</h2>
<p>Václav Havel—<strong>playwright, dissident, and later president</strong>—left a living map across Prague. This tour traces places where <em>art, civic courage, and history</em> intersect.</p>
<h3>Rašín Embankment: Beginnings and Symbols</h3>
<p>We begin at <strong>Rašínovo nábřeží</strong>, near Havel’s former residence. Here, you’ll notice symbolic details: a <em>female relief</em>, motifs of the <em>communist past</em>, and a <em>globe on the roof</em>—a nod to Havel’s global reach.</p>
<h3>Quiet Refuge by the River</h3>
<p>At the <strong>Vltava restaurant</strong>, Havel often sat in the back, facing the wall—an echo of surveillance times. It was a space for <em>thinking and writing</em>, away from the stage of politics.</p>
<div class="blog-inline-image"><img src="/images/blog-havel-2.jpg" alt="Václav Havel tour – riverside reflections" loading="lazy" /></div>
<h3>Café Slavia and Olga</h3>
<p>We visit the legendary <strong>Café Slavia</strong>—a center of intellectual life. Here Havel met artists, politicians, and his great love, <em>Olga</em>. Their bond, rooted in everyday humanity, anchored Havel’s extraordinary public life.</p>
<h3>Dissident Years and Theatres</h3>
<p>We explore Havel’s creative ground at <strong>Divadlo Na zábradlí</strong> and the difficult chapters of <em>interrogations and prison</em> near <strong>Bartolomějská</strong> (today’s Unitas Hotel).</p>
<h3>From Civic Forum to the Castle</h3>
<p>At <strong>Laterna magika</strong>, the <em>Civic Forum</em> held daily briefings during the Velvet Revolution. On <strong>Wenceslas Square</strong> and <strong>Národní</strong>, we recall 17 November. Finally, we reflect in the <strong>Prague Castle</strong> courtyards—symbols reshaped by a playwright-president.</p>
<h3>Why This Route Matters</h3>
<p>Beyond plaques and dates, we seek the <strong>citizen Havel</strong>—humorous, open, speaking with all walks of life. This is a city walk about <em>holding onto humanity under pressure</em>.</p>
<p class="lead">Want the full route or to explore some places on your own? <a href="/book?tour=V%C3%A1clav%20Havel%20Tour:%20Sites%20of%20Freedom#contact-title">Message me</a> and I’ll tailor it to your time and interests.</p>
<p><em>Further reading:</em> <a href="https://www.pragerzeitung.cz/typisch-havel/" target="_blank" rel="noopener">Prager Zeitung: Typisch Havel</a></p>`,
    de: `<h2>Auf den Spuren von Václav Havel</h2>
<p>In Prag finden sich vielerorts Erinnerungen an Václav Havel—<strong>Dichter, Dissident und später Präsident</strong>. Diese Führung verbindet Orte, an denen sich <em>Kunst, Zivilcourage und Geschichte</em> berühren.</p>
<h3>Rašín-Ufer: Anfänge und Symbole</h3>
<p>Wir beginnen am <strong>Rašínovo nábřeží</strong>, beim ehemaligen Wohnhaus Havels. Hier begegnen uns Zeichen seines Lebens: ein <em>Frauenrelief</em>, Spuren der <em>kommunistischen Vergangenheit</em> und eine <em>Erdkugel auf dem Dach</em>—ein Sinnbild seines weltweiten Einflusses.</p>
<h3>Ruhiger Zufluchtsort am Fluss</h3>
<p>Im Restaurant <strong>Vltava</strong> saß Havel oft hinten rechts, mit dem Blick zur Wand—ein Reflex aus Zeiten der Überwachung. Ein Ort zum <em>Nachdenken und Schreiben</em>, fern der politischen Bühne.</p>
<div class="blog-inline-image"><img src="/images/blog-havel-2.jpg" alt="Václav-Havel-Tour – Blick ans Flussufer" loading="lazy" /></div>
<h3>Café Slavia und Olga</h3>
<p>Im legendären <strong>Café Slavia</strong> traf Havel Künstler, Politiker und seine große Liebe <em>Olga</em>. Ihre Verbundenheit, geerdet im Alltäglichen, trug sein außergewöhnliches öffentliches Leben.</p>
<h3>Dissidentenjahre und Theater</h3>
<p>Wir sprechen über Havels kreatives Umfeld im <strong>Divadlo Na zábradlí</strong> und über die dunklen Kapitel—<em>Verhöre und Haft</em> in der Nähe der <strong>Bartolomějská</strong> (heutiges Unitas-Hotel).</p>
<h3>Vom Bürgerforum zur Burg</h3>
<p>In der <strong>Laterna magika</strong> gab das <em>Bürgerforum</em> tägliche Pressebriefings während der Samtenen Revolution. Auf dem <strong>Wenzelsplatz</strong> und an der <strong>Nationalallee (Národní)</strong> erinnern wir an den 17. November. Abschließend betrachten wir in den Höfen der <strong>Prager Burg</strong> neu gedeutete Symbole—ein Dramatiker als Präsident.</p>
<h3>Warum diese Route bewegt</h3>
<p>Neben Daten und Tafeln geht es um den <strong>Bürger Havel</strong>: humorvoll, offen, im Gespräch mit allen Gesellschaftsschichten. Dieser Stadtspaziergang handelt davon, <em>Menschlichkeit unter Druck zu bewahren</em>.</p>
<p class="lead">Möchten Sie die vollständige Route oder einzelne Orte auch selbst erkunden? <a href="/book?tour=V%C3%A1clav%20Havel%20Tour:%20Orte%20der%20Freiheit#contact-title">Schreiben Sie mir</a>—ich passe die Tour an Ihre Zeit und Interessen an.</p>
<p><em>Weiterlesen:</em> <a href="https://www.pragerzeitung.cz/typisch-havel/" target="_blank" rel="noopener">Prager Zeitung: Typisch Havel</a></p>`
  },

  // New Post 7: Did you know? Prague’s Most Beautiful Library (Klementinum)
  'blog.post7.title': {
    en: 'Did You Know? Prague’s Most Beautiful Library is at the Klementinum',
    de: 'Wussten Sie schon? Prags schönste Bibliothek ist im Klementinum',
  },
  'blog.post7.excerpt': {
    en: 'Step into the Baroque Library Hall at the Klementinum—often called one of the most beautiful libraries in the world. Here’s how to see it and what to know.',
    de: 'Betreten Sie den Barocksaal des Klementinums—von vielen als eine der schönsten Bibliotheken der Welt bezeichnet. So sehen Sie sie und das sollten Sie wissen.',
  },
  'blog.post7.date': {
    en: 'October 18, 2025',
    de: '18. Oktober 2025',
  },
  'blog.post7.content': {
    en: `<h2>Klementinum: A Baroque Treasure</h2>
<p>Hidden in the heart of Prague’s Old Town, the <strong>Klementinum</strong> houses a <em>Baroque Library Hall</em> that many consider <strong>one of the most beautiful libraries</strong> in the world. Frescoed ceilings, antique globes, oak bookcases—it’s a postcard of knowledge and art.</p>
<h3>What You’ll See</h3>
<ul>
  <li><strong>Baroque Library Hall:</strong> A museum-like space with historical volumes, celestial globes, and allegorical frescoes.</li>
  <li><strong>Astronomical Tower:</strong> Panoramic views over Prague and the Vltava.</li>
  <li><strong>Meridian Room:</strong> A fascinating look at timekeeping before modern technology.</li>
  </ul>
<div class="did-you-know"><h4>Did you know?</h4><p>The Klementinum once hosted one of Central Europe’s oldest weather stations—continuous measurements began here centuries ago.</p></div>
<div class="blog-inline-image"><img src="/images/klementinum-library-2.jpg" alt="Klementinum Baroque Library—mid view" loading="lazy" /></div>
<h3>How to Visit</h3>
<p>Visits run by guided tour with fixed time slots. Capacity is limited and <strong>photos may be restricted</strong>—please check current rules. I can help <a href="/contact#contact-title">book tickets</a> and combine the Klementinum with a <a href="/tours">tailored Old Town route</a>.</p>
<h3>Tips</h3>
<ul>
  <li>Arrive 10–15 minutes early for your slot.</li>
  <li>Bring binoculars to enjoy ceiling details.</li>
  <li>Consider sunset timing for the Astronomical Tower.</li>
  </ul>
<p><strong>Official resource:</strong> <a href="https://prague.eu/en/objevujte/the-clementinum-astronomical-tower-and-baroque-library/" target="_blank" rel="noopener">Prague.eu — Klementinum Astronomical Tower & Baroque Library</a>. Read more there and you can even book a city-guided tour.</p>
<p class="lead">Want a private walk that ends at the Klementinum with tower views? <a href="/book?tour=Custom%20Private%20Tour#contact-title">Request a bespoke route</a>.</p>`,
    de: `<h2>Klementinum: Ein barockes Juwel</h2>
<p>Mitten in der Prager Altstadt befindet sich das <strong>Klementinum</strong> mit seinem <em>Barocksaal</em>, den viele als <strong>eine der schönsten Bibliotheken</strong> der Welt ansehen. Fresken, historische Globen, Eichenregale—ein Postkartenmotiv von Wissen und Kunst.</p>
<h3>Was Sie sehen</h3>
<ul>
  <li><strong>Barocksaal:</strong> Museumsartiger Raum mit historischen Bänden, Himmelsgloben und allegorischen Fresken.</li>
  <li><strong>Astronomischer Turm:</strong> Panorama über Prag und die Moldau.</li>
  <li><strong>Meridianzimmer:</strong> Spannender Blick auf die Zeitmessung vor der Moderne.</li>
  </ul>
<div class="did-you-know"><h4>Wussten Sie schon?</h4><p>Im Klementinum befindet sich eine der ältesten Wetterstationen Mitteleuropas—kontinuierliche Messungen begannen hier bereits vor Jahrhunderten.</p></div>
<div class="blog-inline-image"><img src="/images/klementinum-library-2.jpg" alt="Klementinum Barockbibliothek—Mitte" loading="lazy" /></div>
<h3>Besuch</h3>
<p>Besichtigungen erfolgen per Führung mit festen Zeitfenstern. Die Kapazität ist begrenzt und <strong>Fotografie kann eingeschränkt</strong> sein—bitte aktuelle Regeln prüfen. Gern helfe ich bei der <a href="/contact#contact-title">Ticketbuchung</a> und kombiniere das Klementinum mit einer <a href="/tours">maßgeschneiderten Altstadtroute</a>.</p>
<h3>Tipps</h3>
<ul>
  <li>10–15 Minuten vor dem Termin eintreffen.</li>
  <li>Ein Fernglas für Deckendetails mitnehmen.</li>
  <li>Für den Turm ggf. Sonnenuntergang einplanen.</li>
  </ul>
<p><strong>Offizielle Ressource:</strong> <a href="https://prague.eu/de/objevujte/astronomischer-turm-und-barocke-bibliothek-des-klementinums/" target="_blank" rel="noopener">Prague.eu — Astronomischer Turm & Barockbibliothek</a>. Dort können Sie mehr lesen und auch eine Stadtführung buchen.</p>
<p class="lead">Möchten Sie einen privaten Spaziergang mit Abschluss im Klementinum und Turmblick? <a href="/book?tour=Individuelle%20Privattour#contact-title">Buchungsanfrage senden</a>.</p>`
  },

  // New Post 8: Klementinum Astronomical Tower & Meridian Room
  'blog.post8.title': {
    en: 'Klementinum Astronomical Tower: Views, Time, and Stories',
    de: 'Astronomischer Turm im Klementinum: Aussicht, Zeit und Geschichten',
  },
  'blog.post8.excerpt': {
    en: 'Climb the Klementinum’s Astronomical Tower for sweeping views, then discover the Meridian Room—where time was once measured by sunlight.',
    de: 'Erklimmen Sie den Astronomischen Turm des Klementinums und entdecken Sie das Meridianzimmer—wo Zeit einst mit Sonnenlicht gemessen wurde.',
  },
  'blog.post8.date': {
    en: 'October 18, 2025',
    de: '18. Oktober 2025',
  },
  'blog.post8.content': {
    en: `<h2>From Baroque Science to Skyline Vistas</h2>
<p>The <strong>Astronomical Tower</strong> at the Klementinum blends baroque science with breathtaking views. After the climb, the reward is a <em>360° panorama</em>—Prague Castle, Charles Bridge, Old Town roofs.</p>
<div class="blog-inline-image"><img src="/images/klementinum-tower-2.jpg" alt="Klementinum Astronomical Tower—panoramic view" loading="lazy" /></div>
<h3>The Meridian Room</h3>
<p>Before atomic clocks, time was set here using a <strong>meridian line</strong> and a ray of the sun. It’s a rare window into how cities coordinated daily life.</p>
<div class="did-you-know"><h4>Did you know?</h4><p>Observations at the Klementinum helped standardize noon in Prague—long before radio signals and GPS.</p></div>
<h3>Practical Notes</h3>
<ul>
  <li>Stairs only; the climb is moderate but steady.</li>
  <li>Weather can affect visibility—clear afternoons are ideal.</li>
  <li>Combine with nearby <a href="/tours">Old Town highlights</a> for a half-day route.</li>
  </ul>
<p><strong>Official resource:</strong> <a href="https://prague.eu/en/objevujte/the-clementinum-astronomical-tower-and-baroque-library/" target="_blank" rel="noopener">Prague.eu — Klementinum Astronomical Tower & Baroque Library</a>. Read more and you can book a city-guided tour there.</p>
<p class="lead">Interested in a private Klementinum-focused tour? <a href="/book?tour=Custom%20Private%20Tour#contact-title">Get in touch</a> and I’ll plan the perfect timing.</p>`,
    de: `<h2>Von barocker Wissenschaft zum Skyline-Blick</h2>
<p>Der <strong>Astronomische Turm</strong> des Klementinums verbindet barocke Wissenschaft mit großartigen Ausblicken. Nach dem Aufstieg wartet ein <em>360°-Panorama</em>—Prager Burg, Karlsbrücke, Dächer der Altstadt.</p>
<div class="blog-inline-image"><img src="/images/klementinum-tower-2.jpg" alt="Astronomischer Turm im Klementinum—Panoramablick" loading="lazy" /></div>
<h3>Das Meridianzimmer</h3>
<p>Vor der Zeit der Atomuhren wurde hier mit <strong>Meridianlinie</strong> und Sonnenstrahl die Zeit bestimmt. Ein seltener Einblick, wie Städte das tägliche Leben koordinierten.</p>
<div class="did-you-know"><h4>Wussten Sie schon?</h4><p>Beobachtungen im Klementinum halfen, den Mittagszeitpunkt in Prag zu standardisieren—lange vor Funksignalen und GPS.</p></div>
<h3>Praktisches</h3>
<ul>
  <li>Nur Treppen; der Aufstieg ist moderat, aber stetig.</li>
  <li>Wetter beeinflusst die Sicht—klare Nachmittage sind ideal.</li>
  <li>Lässt sich gut mit <a href="/tours">Highlights der Altstadt</a> zu einer Halbtagestour verbinden.</li>
  </ul>
<p><strong>Offizielle Ressource:</strong> <a href="https://prague.eu/de/objevujte/astronomischer-turm-und-barocke-bibliothek-des-klementinums/" target="_blank" rel="noopener">Prague.eu — Astronomischer Turm & Barockbibliothek</a>. Dort finden Sie weitere Informationen und Stadtführungen.</p>
<p class="lead">Interessiert an einer privaten Klementinum-Tour? <a href="/book?tour=Individuelle%20Privattour#contact-title">Kontaktieren Sie mich</a>—ich plane das perfekte Timing.</p>`
  },

  // New Post 9: What to do in Prague in November (SEO optimized)
  'blog.post9.title': {
    en: 'What to Do in Prague in November (2025 Guide)',
    de: 'Was man im November in Prag tun kann (2025 Guide)',
  },
  'blog.post9.excerpt': {
    en: 'Visiting Prague in November? Here are the best things to do now—seasonal events, concerts, exhibitions, and cozy city walks before the Christmas markets.',
    de: 'Sie sind im November in Prag? Hier sind die besten Aktivitäten—aktuelle Events, Konzerte, Ausstellungen und gemütliche Stadtrundgänge vor den Weihnachtsmärkten.',
  },
  'blog.post9.date': {
    en: 'October 16, 2025',
    de: '16. Oktober 2025',
  },
  'blog.post9.content': {
    en: `<h2>Prague in November: Cozy Culture and Fewer Crowds</h2>
<p><strong>Prague never sleeps</strong>—even in late autumn, the city is alive with <em>concerts, exhibitions, theatre</em> and warm cafés. November is ideal for <strong>Old Town walks</strong> and <strong>Prague Castle courtyards</strong> with fewer visitors.</p>
<h3>Top Things to Do Now</h3>
<ul>
  <li><strong>Classical music and jazz</strong> in historic venues around Old Town and Malá Strana.</li>
  <li><strong>Gallery & museum visits</strong>—perfect for cooler days (DOX, National Gallery, Jewish Museum).</li>
  <li><strong>Riverside evening walks</strong> for golden lights and castle silhouettes.</li>
  <li><strong>Warm-up cafés</strong> with strudel and hot chocolate off the tourist trail.</li>
  </ul>
<div class="did-you-know"><h4>Did you know?</h4><p>November is one of Prague’s <strong>most affordable</strong> months for accommodation, with excellent availability and fewer queues at major sights.</p></div>
<h3>What’s On in November (Events)</h3>
<p>For a curated list of <strong>current November events in Prague</strong> (concerts, theatre, festivals), see the excellent guide at
<a href="https://www.pragueexperience.com/events/events.asp?EventYear=2025&EventMonth=11" target="_blank" rel="noopener">PragueExperience — Prague Events November 2025</a>.</p>
<h3>Weather & What to Pack</h3>
<p>Expect cool temperatures (5–10°C), occasional wind and early sunsets. Bring layers, a waterproof jacket, and comfortable shoes for cobblestones.</p>
<h3>Related Post</h3>
<p>Also see: <a href="/blog/prague-at-night-magical-experience">Prague at Night: A Magical Experience</a> for evening ideas.</p>
<h3>Plan a Private Tour</h3>
<p class="lead">If you want to explore <strong>Prague’s history</strong> and hidden courtyards with a certified guide, <a href="/book?tour=Custom%20Private%20Tour#contact-title">book a private tour</a>. I’ll tailor a route—<em>Old Town & Jewish Quarter</em>, <em>hidden gems</em>, or a <em>Velvet Revolution</em> theme—optimized for November daylight.</p>
<h3>#tags</h3>
<p>#whatToDoInPragueInNovember #PragueEventsNovember #thingsToDoInPragueNow #PragueNovemberWeather #PragueInNovember #PragueConcerts #PragueExhibitions #PragueItinerary #VisitPrague #PragueTravelGuide</p>`,
    de: `<h2>Prag im November: Kultur & Gemütlichkeit</h2>
<p><strong>Prag schläft nie</strong>—auch im Spätherbst pulsiert die Stadt mit <em>Konzerten, Ausstellungen, Theater</em> und warmen Cafés. Der November eignet sich für <strong>Altstadtspaziergänge</strong> und <strong>Höfe der Prager Burg</strong> mit weniger Besuchern.</p>
<h3>Beste Aktivitäten jetzt</h3>
<ul>
  <li><strong>Klassik & Jazz</strong> in historischen Sälen rund um Altstadt und Kleinseite.</li>
  <li><strong>Galerien & Museen</strong>—ideal für kühlere Tage (DOX, Nationalgalerie, Jüdisches Museum).</li>
  <li><strong>Abendspaziergänge am Fluss</strong> für goldene Lichter und Schlosssilhouette.</li>
  <li><strong>Wärmende Cafés</strong> mit Strudel und heißer Schokolade abseits der Touristenpfade.</li>
  </ul>
<div class="did-you-know"><h4>Wussten Sie schon?</h4><p>Der November gehört in Prag zu den <strong>preiswerteren</strong> Reisemonaten—beste Verfügbarkeiten und kürzere Schlangen an wichtigen Sehenswürdigkeiten.</p></div>
<h3>Was ist los im November (Events)</h3>
<p>Eine ausgezeichnete Übersicht über <strong>aktuelle Prager November-Events</strong> (Konzerte, Theater, Festivals) finden Sie hier:
<a href="https://www.pragueexperience.com/events/events.asp?EventYear=2025&EventMonth=11" target="_blank" rel="noopener">PragueExperience — Prague Events November 2025</a>.</p>
<h3>Wetter & Packliste</h3>
<p>Rechnen Sie mit kühlen Temperaturen (5–10°C), gelegentigem Wind und frühen Sonnenuntergängen. Schichtenprinzip, Regenjacke und bequeme Schuhe für Kopfsteinpflaster.</p>
<h3>Private Führung planen</h3>
<p class="lead">Wenn Sie <strong>Prags Geschichte</strong> und versteckte Höfe mit einer zertifizierten Führerin erleben möchten, <a href="/book?tour=Individuelle%20Privattour#contact-title">buchen Sie eine private Tour</a>. Ich passe die Route an—<em>Altstadt & Jüdisches Viertel</em>, <em>versteckte Schätze</em> oder <em>Samtene Revolution</em>—optimiert für das Novemberlicht.</p>
<h3>#tags</h3>
<p>#wasTunInPragImNovember #PragEventsNovember #wasMachenInPrag #PragNovemberWetter #PragImNovember #PragKonzerte #PragAusstellungen #PragReiseroute #BesuchePrag #PragReiseführer</p>`
  },

  // New Post 10: Top Prague Gardens to Visit (around Prague Castle)
  'blog.post10.title': {
    en: 'Must-Visit Prague Gardens Around Prague Castle (Prettiest Views)',
    de: 'Must-See: Die schönsten Prager Gärten rund um die Prager Burg',
  },
  'blog.post10.excerpt': {
    en: 'From the Royal Garden to the terraced Palace Gardens—discover the prettiest, must-visit Prague gardens near the Castle for views, history, and quiet walks.',
    de: 'Von der Königlichen Gartenanlage bis zu den Terrassengärten—die schönsten, sehenswertesten Prager Gärten nahe der Burg für Ausblicke, Geschichte und stille Spaziergänge.',
  },
  'blog.post10.date': {
    en: 'October 18, 2025',
    de: '18. Oktober 2025',
  },
  'blog.post10.content': {
  en: `<h2>Prettiest, Must-Visit Prague Gardens Near the Castle</h2>
<p>Looking for the <strong>best Prague gardens</strong> and <strong>must-visit green spaces</strong> with views and history? Around <strong>Prague Castle</strong> you’ll find baroque terraces, royal lawns, and quiet hideaways. Here’s a curated list to plan your visit.</p>
<h3>Royal Garden (Královská zahrada)</h3>
<p>Created in the 16th century with the famous <strong>Singing Fountain</strong> and the elegant <strong>Ball Game Hall</strong>. A peaceful lawn with seasonal flowerbeds.</p>
<h3>South Gardens (Jižní zahrady)</h3>
<p>A chain of castle-side viewpoints above the city—including the <em>Paradise Garden</em>, <em>Garden on the Bastion</em>, and <em>Hartig Garden</em>. Golden hour is superb.</p>
<h3>Deer Moat (Jelení příkop)</h3>
<p>A natural ravine below the castle walls, ideal for a quiet <strong>green walk</strong> when open (seasonal access).</p>
<div class="blog-inline-image"><img src="/images/blog-gardens-2.jpg" alt="Terraced palace gardens below Prague Castle" loading="lazy" /><div class="image-credit">Photo: Roman Boed</div></div>
<h3>Palace Gardens below Prague Castle (Zahrady pod Pražským hradem)</h3>
<p>A group of <strong>terraced baroque gardens</strong> climbing the slope beneath the castle—ornamental staircases, statues, and classic red roofs in the background.</p>
<h3>Vrtba Garden (Vrtbovská zahrada)</h3>
<p>One of Europe’s baroque gems on the Malá Strana side—trimmed hedges, balustrades, and some of the best <strong>castle views</strong> in the city.</p>
<h3>Wallenstein Garden (Valdštejnská zahrada)</h3>
<p>Peacocks, grotto-style dripstone wall, and arcades beside the Senate. Scenic, central, and usually open in the day.</p>
<h3>Strahov & Monastery Gardens</h3>
<p>Terraces near <strong>Strahov Monastery</strong> offer sweeping vistas of the Lesser Town and Old Town spires.</p>
<h3>Related Post</h3>
<p>Photo lovers: <a href="/blog/top-21-instagrammable-places-in-prague">Top 21 Instagrammable Places in Prague</a> pairs perfectly with these gardens.</p>
<h3>Season & Tips</h3>
<ul>
  <li>Most gardens are <strong>seasonal</strong> (spring–autumn). Check hours before you go.</li>
  <li><strong>Golden hour</strong> and mornings are best for photos and calm.</li>
  <li>Combine 2–3 gardens for a <strong>half‑day route</strong> with viewpoints and cafés.</li>
  </ul>
<p class="lead">Want a garden‑focused private walk with the <strong>best photo spots</strong>? <a href="/book?tour=Custom%20Private%20Tour#contact-title">Book a private tour</a> and I’ll plan an easy loop around Prague Castle and Malá Strana.</p>
<h3>#tags</h3>
<p>#PragueGardens #MustVisitPrague #PrettiestPragueGardens #RoyalGarden #VrtbaGarden #WallensteinGarden #PalaceGardens #PragueCastleViewpoints #HiddenGemsPrague #VisitPrague #PragueItinerary #PraguePhotoSpots</p>`,
  de: `<h2>Must-See: Die schönsten Gärten nahe der Burg</h2>
<p>Suchen Sie die <strong>besten Prager Gärten</strong> und <strong>sehenswerten grünen Oasen</strong> mit Aussicht und Geschichte? Rund um die <strong>Prager Burg</strong> finden Sie barocke Terrassen, königliche Rasenflächen und stille Rückzugsorte. Hier eine kuratierte Übersicht.</p>
<h3>Königlicher Garten (Královská zahrada)</h3>
<p>Im 16. Jahrhundert angelegt—mit dem berühmten <strong>Singenden Brunnen</strong> und der eleganten <strong>Ballspielhalle</strong>. Ruhige Wiesen und saisonale Beete.</p>
<h3>Südgärten (Jižní zahrady)</h3>
<p>Eine Kette von Aussichtspunkten direkt am Burgmassiv—darunter <em>Paradiesgarten</em>, <em>Garten auf dem Bastion</em> und <em>Hartig-Garten</em>. Ideal zur Goldenen Stunde.</p>
<h3>Hirschgraben (Jelení příkop)</h3>
<p>Eine natürliche Senke unterhalb der Burgmauern—ruhiger <strong>grüner Spaziergang</strong>, wenn geöffnet (saisonal).</p>
<div class="blog-inline-image"><img src="/images/blog-gardens-2.jpg" alt="Terrassierte Palastgärten unter der Prager Burg" loading="lazy" /><div class="image-credit">Foto: Roman Boed</div></div>
<h3>Palastgärten unter der Prager Burg (Zahrady pod Pražským hradem)</h3>
<p>Ein Ensemble <strong>barocker Terrassengärten</strong> am Burgabhang—ornamentale Treppen, Statuen und klassische Dächerkulisse.</p>
<h3>Vrtba-Garten (Vrtbovská zahrada)</h3>
<p>Eines der barocken Schmuckstücke auf der Kleinseite—geschnittene Hecken, Balustraden und <strong>beste Schlossblicke</strong>.</p>
<h3>Wallenstein-Garten (Valdštejnská zahrada)</h3>
<p>Pfauen, Tropfsteinwand und Arkaden am Senat. Zentral und tagsüber meist zugänglich.</p>
<h3>Strahov & Klostergärten</h3>
<p>Terrassen beim <strong>Strahover Kloster</strong> mit weitem Blick über Kleinseite und Altstadt.</p>
<h3>Saison & Tipps</h3>
<ul>
  <li>Viele Gärten sind <strong>saisonal</strong> (Frühling–Herbst). Öffnungszeiten vorab prüfen.</li>
  <li><strong>Goldene Stunde</strong> und Morgen eignen sich am besten für Ruhe und Fotos.</li>
  <li>Kombinieren Sie 2–3 Gärten zu einer <strong>Halbtagsroute</strong> mit Ausblicken und Caféstopps.</li>
  </ul>
<p class="lead">Möchten Sie einen <strong>gartenfokussierten Privatspaziergang</strong> mit den besten Fotospots? <a href="/book?tour=Individuelle%20Privattour#contact-title">Private Tour buchen</a>—ich plane eine leichte Runde um Burg und Kleinseite.</p>
<h3>#tags</h3>
<p>#PragerGärten #MustSeePrag #SchönstePragerGärten #KöniglicherGarten #VrtbaGarten #WallensteinGarten #Palastgärten #BurgAussicht #VersteckteSchätzePrag #BesuchePrag #PragReiseroute #PragFotospots</p>`
  },
 
  // New Post 11: Strahov Monastery
  'blog.post11.title': {
    en: 'Strahov Monastery: Views, Libraries, and Quiet Beauty',
    de: 'Strahover Kloster: Aussicht, Bibliotheken und stille Schönheit',
  },
  'blog.post11.excerpt': {
    en: 'Explore Strahov Monastery above Prague Castle—panoramic views, baroque library halls (with permits), and serene courtyards near Petřín.',
    de: 'Entdecken Sie das Strahover Kloster oberhalb der Prager Burg—Panorama, barocke Bibliothekssäle (mit Genehmigung) und stille Höfe nahe dem Petřín.',
  },
  'blog.post11.date': {
    en: 'October 19, 2025',
    de: '19. Oktober 2025',
  },
  'blog.post11.content': {
    en: `<h2>Strahov Monastery Above the City</h2>
<p><strong>Strahov Monastery (Strahovský klášter)</strong> crowns the ridge above <a href="/tours">Prague Castle</a>. It’s perfect for <em>panoramic views</em>, a peaceful walk between <strong>Petřín</strong> and Hradčany, and—by special arrangement—glimpses of <strong>baroque library halls</strong>.</p>
<div class="blog-inline-image"><img src="/images/strahov-monastery.jpg" alt="Strahov Monastery with Prague panorama" loading="lazy" /></div>
<h3>What to See</h3>
<ul>
  <li><strong>Panoramic terrace:</strong> One of the best <em>castle-and-city</em> vistas, especially near golden hour.</li>
  <li><strong>Courtyards & Basilica:</strong> Quiet spaces around the <em>Basilica of the Assumption</em> with centuries of history.</li>
  <li><strong>Strahov Library:</strong> Famous <em>Philosophical</em> and <em>Theological</em> Halls—access rules and photography <strong>vary by permit</strong>.</li>
  <li><strong>Strahov Brewery:</strong> A traditional stop for refreshments after the walk.</li>
  </ul>
<div class="did-you-know"><h4>Did you know?</h4><p>Strahov’s library collections include <strong>globes and curiosities</strong> similar in spirit to the <a href="/blog/klementinum-baroque-library-prague">Klementinum Baroque Library</a>.</p></div>
<h3>Route Ideas</h3>
<p>Combine Strahov with <a href="/blog/top-prague-gardens-to-visit">Palace & Monastery gardens</a> for a scenic loop, or descend through the <em>Lesser Town</em> towards <a href="/blog/top-21-instagrammable-places-in-prague">iconic photo spots</a>.</p>
<h3>Practical Tips</h3>
<ul>
  <li>Check <strong>opening hours</strong> for the basilica and library areas; library access often requires a tour or permit.</li>
  <li>Early morning or late afternoon offers softer light and fewer people.</li>
  <li>Wear comfortable shoes—the cobbles and slopes between Petřín and the Castle can be steep.</li>
  </ul>
<p class="lead">Want a peaceful <strong>Strahov + Castle</strong> route with the best viewpoints? <a href="/book?tour=Custom%20Private%20Tour#contact-title">Book a private tour</a> and I’ll tailor the walk to your pace.</p>`,
    de: `<h2>Das Strahover Kloster über der Stadt</h2>
<p>Das <strong>Strahover Kloster (Strahovský klášter)</strong> krönt den Rücken oberhalb der <a href="/tours">Prager Burg</a>. Ideal für <em>Panorama-Blicke</em>, einen ruhigen Spaziergang zwischen <strong>Petřín</strong> und Hradschin und—mit Voranmeldung—Blicke in <strong>barocke Bibliothekssäle</strong>.</p>
<div class="blog-inline-image"><img src="/images/strahov-monastery.jpg" alt="Strahov-Kloster mit Prag-Panorama" loading="lazy" /></div>
<h3>Sehenswürdigkeiten</h3>
<ul>
  <li><strong>Panoramaterrasse:</strong> Einer der schönsten <em>Schloss-und-Stadt</em>-Blicke—besonders zur Goldenen Stunde.</li>
  <li><strong>Höfe & Basilika:</strong> Stille Bereiche rund um die <em>Basilika Mariä Himmelfahrt</em> mit Jahrhunderten Geschichte.</li>
  <li><strong>Strahov-Bibliothek:</strong> Berühmte <em>Philosophische</em> und <em>Theologische</em> Säle—Zutritt und Fotografie <strong>oft nur mit Genehmigung</strong>.</li>
  <li><strong>Strahov-Brauerei:</strong> Traditioneller Stopp für eine Erfrischung nach dem Spaziergang.</li>
  </ul>
<div class="did-you-know"><h4>Wussten Sie schon?</h4><p>Zu den Sammlungen gehören <strong>Globen und Kuriositäten</strong>, verwandt in Geist und Atmosphäre mit der <a href="/blog/klementinum-baroque-library-prague">Barockbibliothek im Klementinum</a>.</p></div>
<h3>Routenideen</h3>
<p>Kombinieren Sie Strahov mit <a href="/blog/top-prague-gardens-to-visit">Palast- und Klostergärten</a> zu einer malerischen Runde, oder steigen Sie über die <em>Kleinseite</em> ab zu <a href="/blog/top-21-instagrammable-places-in-prague">ikonischen Fotospots</a>.</p>
<h3>Praktische Tipps</h3>
<ul>
  <li><strong>Öffnungszeiten</strong> der Basilika und Bibliothek prüfen; Bibliothekszugang oft nur mit Führung oder Genehmigung.</li>
  <li>Morgen oder später Nachmittag bieten sanftes Licht und weniger Besucher.</li>
  <li>Bequeme Schuhe—Pflaster und Steigungen zwischen Petřín und Burg können anspruchsvoll sein.</li>
  </ul>
<p class="lead">Möchten Sie eine ruhige <strong>Strahov + Burg</strong>-Route mit den besten Aussichtspunkten? <a href="/book?tour=Individuelle%20Privattour#contact-title">Private Tour buchen</a>—ich passe den Spaziergang an Ihr Tempo an.</p>`
  },

  // ────── Zuzana Manová personal page ──────

  'zm.hero.h1': {
    en: 'Zuzana Manová – Private Prague Expert Guide',
    de: 'Zuzana Manová – Ihre deutschsprachige Prag-Expertin',
  },
  'zm.hero.subtitle': {
    en: 'Certified expert guide and Jewish Quarter specialist with 40 years of experience. I show you Prague through the eyes of someone who grew up with its streets, its revolutions, and its stories.',
    de: 'Zertifizierte Expertin und Spezialistin für das Jüdische Viertel mit 40 Jahren Erfahrung. Ich zeige Ihnen Prag durch die Augen einer Pragerin – mit Geschichten, die Sie in keinem Reiseführer finden.',
  },
  'zm.bio.title': {
    en: 'About Zuzana Manová',
    de: 'Über Zuzana Manová',
  },
  'zm.bio.p1': {
    en: 'My name is Zuzana Manová and I have been a professional tour guide in Prague since 1986. I studied at the Czech Technical University (Ing.) and spent years working as an engineer before the Velvet Revolution in 1989 opened new possibilities. I pivoted to what I had always been passionate about: sharing my city with visitors from around the world.',
    de: 'Mein Name ist Zuzana Manová und ich bin seit 1986 professionelle Stadtführerin in Prag. Ich habe an der Tschechischen Technischen Universität studiert (Ing.) und als Ingenieurin gearbeitet, bevor die Samtene Revolution 1989 neue Möglichkeiten eröffnete. Seitdem widme ich mich dem, was mich schon immer begeistert hat: meine Stadt mit Besuchern aus aller Welt zu teilen.',
  },
  'zm.bio.p2': {
    en: 'Prague is not just where I work – it is where I grew up, where I lived through Communism, where I joined the revolution, and where I raised my family. When I guide you through the Old Town, across the Charles Bridge, or up to Prague Castle, I am sharing four decades of personal memories alongside centuries of history.',
    de: 'Prag ist nicht nur mein Arbeitsplatz – es ist die Stadt, in der ich aufgewachsen bin, den Kommunismus erlebt habe, an der Revolution teilnahm und meine Familie großgezogen habe. Wenn ich Sie durch die Altstadt, über die Karlsbrücke oder zur Prager Burg führe, teile ich vier Jahrzehnte persönlicher Erinnerungen mit Jahrhunderten von Geschichte.',
  },
  'zm.bio.p3': {
    en: 'As an accredited guide at the Jewish Museum in Prague, I hold a specialist certification that allows me to offer in-depth tours of the Jewish Quarter – from the Old Jewish Cemetery to the synagogues – with the context and sensitivity the subject deserves. I guide in German and English.',
    de: 'Als akkreditierte Führerin am Jüdischen Museum in Prag halte ich eine Spezialisierungszertifizierung, die mir ermöglicht, tiefgehende Führungen durch das Jüdische Viertel anzubieten – vom Alten Jüdischen Friedhof bis zu den Synagogen – mit dem Kontext und der Sensibilität, die das Thema verdient. Ich führe auf Deutsch und Englisch.',
  },
  'zm.cred.1.title': {
    en: 'Certified Expert Guide',
    de: 'Zertifizierte Expertin',
  },
  'zm.cred.1.desc': {
    en: 'Licensed expert guide in the Czech Republic since 1986. Over 40 years of professional guiding experience in Prague.',
    de: 'Zertifizierte Prag-Expertin seit 1986. Über 40 Jahre professionelle Erfahrung als deutschsprachige Stadtführerin.',
  },
  'zm.cred.2.title': {
    en: 'Jewish Quarter Specialist',
    de: 'Spezialistin Jüdisches Viertel',
  },
  'zm.cred.2.desc': {
    en: 'Specialist accreditation from the Jewish Museum in Prague for in-depth tours of all synagogues and the Old Jewish Cemetery.',
    de: 'Akkreditierte Spezialistin des Jüdischen Museums in Prag für tiefgehende Führungen durch alle Synagogen und den Alten Jüdischen Friedhof.',
  },
  'zm.cred.3.title': {
    en: 'German & English',
    de: 'Deutsch & Englisch',
  },
  'zm.cred.3.desc': {
    en: 'Fluent in both languages with native-level Czech – no interpreter needed.',
    de: 'Fließend in beiden Sprachen mit muttersprachlichem Tschechisch – kein Dolmetscher nötig.',
  },
  'zm.offer.title': {
    en: 'What Zuzana Manová Offers',
    de: 'Was Zuzana Manová anbietet',
  },
  'zm.offer.p1': {
    en: 'Every tour with Zuzana Manová is private and tailored to your interests. Whether you have three hours or a full day, I design the route around what matters to you: architecture, history, food, hidden courtyards, or a bit of everything.',
    de: 'Jede Tour mit Zuzana Manová ist privat und auf Ihre Interessen zugeschnitten. Ob Sie drei Stunden oder einen ganzen Tag haben – ich gestalte die Route nach Ihren Wünschen: Architektur, Geschichte, Essen, versteckte Innenhöfe oder von allem etwas.',
  },
  'zm.offer.p2': {
    en: 'I do not use scripts or rush through a checklist. My tours are conversations – and the best ones happen when you ask the questions you genuinely want answered.',
    de: 'Ich verwende keine vorgefertigten Texte und hetze nicht durch eine Checkliste. Meine Touren sind Gespräche – und die besten entstehen, wenn Sie die Fragen stellen, die Sie wirklich interessieren.',
  },
  'zm.tour.1': {
    en: 'Old Town & Jewish Quarter Private Tour',
    de: 'Altstadt & Jüdisches Viertel Privattour',
  },
  'zm.tour.2': {
    en: 'Prague Castle Guided Tour',
    de: 'Prager Burg Führung',
  },
  'zm.tour.3': {
    en: 'Full-Day Prague Experience',
    de: 'Ganztägige Prag-Erfahrung',
  },
  'zm.tour.4': {
    en: 'Custom Private Tour',
    de: 'Individuelle Privattour',
  },
  'zm.faq.title': {
    en: 'Frequently Asked Questions about Zuzana Manová',
    de: 'Häufig gestellte Fragen zu Zuzana Manová',
  },
  'zm.faq.q1': {
    en: 'Who is Zuzana Manová?',
    de: 'Wer ist Zuzana Manová?',
  },
  'zm.faq.a1': {
    en: 'Zuzana Manová (Ing.) is a certified Prague tour guide who has been leading private tours since 1986. She is a native Praguer with accreditation from the Jewish Museum in Prague and offers tours in German and English.',
    de: 'Zuzana Manová (Ing.) ist eine zertifizierte Prager Stadtführerin, die seit 1986 private Führungen anbietet. Sie ist gebürtige Pragerin mit Akkreditierung am Jüdischen Museum in Prag und führt auf Deutsch und Englisch.',
  },
  'zm.faq.q2': {
    en: 'What languages does Zuzana Manová speak?',
    de: 'Welche Sprachen spricht Zuzana Manová?',
  },
  'zm.faq.a2': {
    en: 'Zuzana Manová offers tours in German and English. She is a native Czech speaker and communicates fluently in all three languages during tours.',
    de: 'Zuzana Manová bietet Führungen auf Deutsch und Englisch an. Sie ist tschechische Muttersprachlerin und kommuniziert fließend in allen drei Sprachen während der Touren.',
  },
  'zm.faq.q3': {
    en: 'How can I book a tour with Zuzana Manová?',
    de: 'Wie kann ich eine Tour mit Zuzana Manová buchen?',
  },
  'zm.faq.a3': {
    en: 'You can send a tour inquiry through the booking form on this website, call +420 721 231 933, or send a WhatsApp message. Zuzana typically responds within a few hours.',
    de: 'Sie können eine Touranfrage über das Buchungsformular auf dieser Website senden, unter +420 721 231 933 anrufen oder eine WhatsApp-Nachricht schicken. Zuzana antwortet in der Regel innerhalb weniger Stunden.',
  },
  'zm.faq.q4': {
    en: 'Is Zuzana Manová a certified tour guide?',
    de: 'Ist Zuzana Manová eine zertifizierte Stadtführerin?',
  },
  'zm.faq.a4': {
    en: 'Yes. Zuzana Manová holds an official Czech Republic tour guide license and is additionally accredited by the Jewish Museum in Prague for specialized tours of the Jewish Quarter.',
    de: 'Ja. Zuzana Manová besitzt eine offizielle Stadtführerlizenz der Tschechischen Republik und ist zusätzlich beim Jüdischen Museum in Prag für spezialisierte Führungen durch das Jüdische Viertel akkreditiert.',
  },
  'zm.faq.q5': {
    en: 'What makes Zuzana Manová different from other Prague tour guides?',
    de: 'Was unterscheidet Zuzana Manová von anderen Prager Stadtführern?',
  },
  'zm.faq.a5': {
    en: 'With over 40 years of experience, Zuzana Manová brings personal stories from living through Communism and the Velvet Revolution. Her tours are private, unhurried, and adapted to each visitor. She is one of the longest-serving licensed guides in Prague.',
    de: 'Mit über 40 Jahren Erfahrung bringt Zuzana Manová persönliche Geschichten aus der Zeit des Kommunismus und der Samtenen Revolution ein. Ihre Touren sind privat, ohne Zeitdruck und auf jeden Besucher zugeschnitten. Sie ist eine der am längsten tätigen lizenzierten Stadtführerinnen in Prag.',
  },
  'zm.cta.title': {
    en: 'Discover Prague with Zuzana Manová',
    de: 'Entdecken Sie Prag mit Zuzana Manová',
  },
  'zm.cta.subtitle': {
    en: 'Four decades of stories, one unforgettable tour.',
    de: 'Vier Jahrzehnte voller Geschichten, eine unvergessliche Tour.',
  },
  'nav.zuzana': {
    en: 'About Zuzana',
    de: 'Über Zuzana',
  },
};

export type TranslationKey = keyof typeof translations;

export const translate = (key: TranslationKey, language: 'en' | 'de'): string => {
  // Prefer blog translations from the separate module when available
  if ((blogTranslations as any)[key]) {
    return (blogTranslations as any)[key]?.[language] || key;
  }
  return translations[key]?.[language] || key;
};
